// @ts-nocheck
/**
 * CaseDetailView — Salesforce-style Case record detail.
 *
 * Sections: Case Information, Additional Information, Description Information, System Information.
 * Right sidebar: Chatter / Activities placeholder.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Edit3,
  Plus,
  ArrowRightLeft,
  CheckCircle,
  Search,
  Paperclip,
  Calendar,
  Check,
  Clock,
  RefreshCw,
  Save,
  X,
} from 'lucide-react';
import type { ListViewRecord } from '../../types/listView';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { useCreditBlockFiles, useUploadCreditBlockFile } from '../../services/queries/creditBlockFileQueries';
import {
  fetchUnlockRequestsLinkedToCase,
  getCaseLinkedClientByAccountLookup,
  hydrateCreditBlockCaseOwners,
} from '../../services/queries/creditBlockCaseQueries';
import { getPicklistValues, prefetchFieldMetadata } from '../../services/sfFieldMetadataService';
import { updateRecord } from '../../services/recordStorageService';
import { getCustomSuffix } from '../../config/gobodoConfig';
import SalesforceActivityTab from './SalesforceActivityTab';
import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig from '../../config/gobodoConfig';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { useActivities, useCreateActivity } from '../../hooks/useActivities';
import { findGraphUserBySfId, looksLikeGraphUserSfId } from '../../services/gobodo/gobodoUserSfIdLookup';
import { useCreditBlockBureaus } from '../../services/queries/creditBlockBureauQueries';
import { useSendEmail } from '../../hooks/useSendEmail';
import CaseStatusPath from './CaseStatusPath';
import { useCreditBlockActivitiesForCase } from '../../services/queries/creditBlockActivityQueries';
import { StandardLookupField } from '../standard';
import { getGobodoRecordById, getGobodoRecordByKey } from '../../services/gobodo/gobodoRecordService';
import { formatAuditUserDate } from '../../utils/creditBlockFormatters';
import { usersToCreditBlockAssigneeOptions } from '../../services/gobodo/creditBlockNewCaseGobodoSearch';
import {
  CREDIT_BLOCK_TEAM_USERS_QUERY_KEY,
  fetchCreditBlockTeamUsers,
  getCreditBlockRoleLabel,
  isNameOwnerToken,
} from '../../services/creditBlock/creditBlockAssigneePickerUtils';
import {
  CREDIT_BLOCK_SYSTEM_CASE_ORIGIN,
  normalizeCreditBlockCaseOrigin,
} from '../../services/creditBlock/caseOriginDisplay';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface CaseDetailViewProps {
  caseRecord: ListViewRecord;
  clientName: string;
  onBack: () => void;
  onOpenClientDetails?: (clientLookup: string) => void;
}

/* ------------------------------------------------------------------ */
/*  SF Section (collapsible, blue bottom stripe)                       */
/* ------------------------------------------------------------------ */

const SfSection: React.FC<{
  title: string;
  defaultCollapsed?: boolean;
  children: React.ReactNode;
}> = ({ title, defaultCollapsed = false, children }) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between px-6 py-3 border-b border-gray-200 dark:border-gray-700 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{title}</span>
        {collapsed ? (
          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {!collapsed && <div className="px-6 py-4">{children}</div>}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Read-only SF field (with pencil icon placeholder)                  */
/* ------------------------------------------------------------------ */

const SfField: React.FC<{
  label: string;
  value: React.ReactNode;
  isLink?: boolean;
  showEdit?: boolean;
}> = ({ label, value, isLink = false, showEdit = true }) => {
  const empty = <span className="text-gray-400 dark:text-gray-500 italic">—</span>;
  const display = value == null || value === '' ? empty : value;

  return (
    <div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
      <div className="group flex items-center gap-2 min-h-[1.75rem]">
        <div className={`text-sm flex-1 ${isLink ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'}`}>
          {display}
        </div>
        {showEdit && (
          <button
            type="button"
            className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Edit"
          >
            <Edit3 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Date formatter                                                     */
/* ------------------------------------------------------------------ */

function fmtDate(v: unknown): string {
  if (!v) return '';
  const s = String(v);
  const dateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }) +
    (dateOnly ? '' : ', ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }));
}

/* ------------------------------------------------------------------ */
/*  Chatter feed item                                                  */
/* ------------------------------------------------------------------ */

const FeedItem: React.FC<{
  author: string;
  action: string;
  date: string;
  children?: React.ReactNode;
}> = ({ author, action, date, children }) => (
  <div className="border-b border-gray-200 dark:border-gray-700 pb-4 mb-4 last:border-0 last:mb-0 last:pb-0">
    <div className="flex items-start gap-2">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-300">
        {author.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm">
          <span className="font-medium text-blue-600 dark:text-blue-400">{author}</span>
          {' '}<span className="text-gray-600 dark:text-gray-400">{action}</span>
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{date}</p>
        {children && <div className="mt-2 text-sm bg-gray-50 dark:bg-gray-700/40 rounded p-3 text-gray-700 dark:text-gray-300">{children}</div>}
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Related tab helpers                                                */
/* ------------------------------------------------------------------ */

/** Collapsible related list section header with count + optional New button */
const RelatedSection: React.FC<{
  title: string;
  count: number;
  showNew?: boolean;
  newLabel?: string;
  onNew?: () => void;
  children: React.ReactNode;
}> = ({ title, count, showNew, newLabel = 'New', onNew, children }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title} ({count})</h4>
      {showNew && (
        <button
          type="button"
          onClick={onNew}
          disabled={!onNew}
          className={onNew
            ? 'px-3 py-1 text-xs border border-blue-200 dark:border-blue-700 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30'
            : 'px-3 py-1 text-xs border border-gray-200 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
          }
        >
          {newLabel}
        </button>
      )}
    </div>
    {children}
  </div>
);

/** Empty state */
const EmptyRelatedState: React.FC<{
  section: string;
}> = ({ section }) => (
  <div className="px-4 py-4">
    <p className="text-xs text-gray-500 dark:text-gray-400">No {section.toLowerCase()} records found.</p>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

const CaseDetailView: React.FC<CaseDetailViewProps> = ({
  caseRecord,
  clientName,
  onBack,
  onOpenClientDetails,
}) => {
  const DEFAULT_CASE_STATUS_OPTIONS = ['New', 'Assigned', 'In Progress', 'Waiting on Bureau', 'Escalated', 'On Hold', 'Closed'];
  const DEFAULT_CASE_SUBTYPE_OPTIONS = [
    'ChexSystems Block',
    'ChexSystems Permanent Unblock',
    'ChexSystems Reblock',
    'ChexSystems Unblock',
    'Client Request',
    'Deceased Client',
    'Equifax Block',
    'Equifax Permanent Unblock',
    'Equifax Reblock',
    'Equifax Unblock',
    'Experian Block',
    'Experian Permanent Unblock',
    'Experian Reblock',
    'Experian Unblock',
    'Special Case',
    'TransUnion Block',
    'TransUnion Permanent Unblock',
    'TransUnion Reblock',
    'TransUnion Unblock',
  ];
  const queryClient = useQueryClient();

  /** Map SalesforceActivityTab types to chatter composer actions */
  const mapToolbarActivityToComposer = (type: string): 'email' | 'post' | 'task' | 'event' | 'call' => {
    if (type === 'meeting') return 'event';
    if (type === 'note') return 'post';
    if (type === 'email' || type === 'task' || type === 'call') return type;
    return 'post';
  };

  const [sidebarTab, setSidebarTab] = useState<'chatter' | 'activities'>('chatter');
  const [recordTab, setRecordTab] = useState<'related' | 'details'>('details');
  const [chatterAction, setChatterAction] = useState<'email' | 'post' | 'task' | 'event' | 'call'>('post');
  const caseFileInputRef = useRef<HTMLInputElement>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [editLookupLabel, setEditLookupLabel] = useState('');
  const [metaVersion, setMetaVersion] = useState(0);
  const [localCaseValues, setLocalCaseValues] = useState<Record<string, string>>({});
  const [localOwnerLabel, setLocalOwnerLabel] = useState('');
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferSaving, setTransferSaving] = useState(false);
  const [composerSubject, setComposerSubject] = useState('');
  const [composerBody, setComposerBody] = useState('');
  const [composerDueDate, setComposerDueDate] = useState('');
  const [composerLocation, setComposerLocation] = useState('');
  const [composerError, setComposerError] = useState<string | null>(null);
  const [activityToast, setActivityToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [composerSavedAt, setComposerSavedAt] = useState(0);
  const [isCaseEditModalOpen, setIsCaseEditModalOpen] = useState(false);
  const [caseEditValues, setCaseEditValues] = useState<Record<string, string>>({});
  const [caseEditOwnerLabel, setCaseEditOwnerLabel] = useState('');
  const [caseEditParentCaseLabel, setCaseEditParentCaseLabel] = useState('');
  const [caseEditError, setCaseEditError] = useState<string | null>(null);
  const [caseEditSaving, setCaseEditSaving] = useState(false);
  const [workflowError, setWorkflowError] = useState<string | null>(null);
  const [workflowNotice, setWorkflowNotice] = useState<string | null>(null);
  const [statusDebug, setStatusDebug] = useState<{
    requested: string;
    server: string;
    rawStatus: string;
    rawCanonical: string;
    rawSuffixed: string;
  } | null>(null);
  const [workflowBusy, setWorkflowBusy] = useState(false);
  const [manualEmailTo, setManualEmailTo] = useState('');
  const [manualEmailCc, setManualEmailCc] = useState('');
  const [manualEmailSubject, setManualEmailSubject] = useState('');
  const [manualEmailBody, setManualEmailBody] = useState('');
  const [newCommentBody, setNewCommentBody] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [closeCaseCommentRequired, setCloseCaseCommentRequired] = useState(false);
  const [resolvedActivityUserNames, setResolvedActivityUserNames] = useState<Record<string, string>>({});
  const { createActivity, loading: creatingActivity } = useCreateActivity();
  const sendEmailMutation = useSendEmail();
  const suffix = getCustomSuffix();
  const caseKey = String(caseRecord.key ?? caseRecord.id ?? '').trim();
  const { data: caseFiles = [], isLoading: caseFilesLoading } = useCreditBlockFiles(caseKey, recordTab === 'related');
  const uploadCaseFile = useUploadCreditBlockFile(caseKey);
  useEffect(() => {
    let mounted = true;
    prefetchFieldMetadata('Case')
      .catch(() => null)
      .finally(() => {
        if (mounted) setMetaVersion((v) => v + 1);
      });
    return () => {
      mounted = false;
    };
  }, []);
  const { data: hydratedOwnerRows } = useQuery({
    // Identity-only queryKey. Previously included caseRecord.owner and
    // caseRecord.ownerSfId which are derived values that change identity
    // whenever the parent re-renders with a freshly-spread caseRecord
    // object (even when the underlying owner is unchanged). That caused
    // refetch storms on every parent re-render. The hydration function
    // reads the latest owner directly off caseRecord so the value-correct
    // result is still produced — only the queryKey needs to be stable.
    queryKey: ['credit-block', 'case-detail-owner', caseRecord.id ?? caseKey],
    queryFn: async () => hydrateCreditBlockCaseOwners([caseRecord]),
    enabled: shouldUseGobodo() && Boolean(caseRecord.id || caseKey),
    staleTime: 2 * 60 * 1000,
  });
  const c = hydratedOwnerRows?.[0] ?? caseRecord;
  const accountLookup = String(
    c.accountId ??
      c.AccountId__c ??
      c.AccountId ??
      c.Client__c ??
      c.clientId ??
      c.relatedClientId ??
      ''
  ).trim();
  const { data: linkedClientInfo } = useQuery({
    queryKey: ['credit-block', 'case-detail-linked-client', caseRecord.id, caseKey, accountLookup],
    queryFn: () => getCaseLinkedClientByAccountLookup(accountLookup),
    enabled: shouldUseGobodo() && Boolean(accountLookup),
    staleTime: 5 * 60 * 1000,
  });
  const traversalStartCaseKey = String((c as { key?: unknown }).key ?? caseKey).trim();
  const parsedCaseNumericId = (() => {
    const candidates = [c.id, caseRecord.id];
    for (const candidate of candidates) {
      if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate;
      const text = String(candidate ?? '').trim();
      if (/^\d+$/.test(text)) return Number(text);
    }
    return 0;
  })();
  const hasNumericTraversalStart = parsedCaseNumericId > 0;

  // Fetch CaseHistory records via traversal (Case → CaseHistory)
  const { data: gobodoCaseHistory = [] } = useQuery({
    queryKey: ['credit-block', 'case-history', parsedCaseNumericId || traversalStartCaseKey],
    queryFn: async () => {
      const apiClient = getGobodoClient();
      const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);
      const input = new GobodoApi.GraphTraversalInput();
      input.setGraphId(GobodoConfig.graph());
      if (hasNumericTraversalStart) input.setStartObjectId(parsedCaseNumericId);
      else input.setStartObjectKey(traversalStartCaseKey);
      input.setRelationshipTypes([`case_history_rel${suffix}`]);
      input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
      input.setMaxPathLength(1);
      const result = await retryWithBackoff(() => traversalApi.traverseObjects(input), { maxRetries: 2, initialDelayMs: 1000 });
      if (!Array.isArray(result)) return [];
      return result.map((r) => {
        const d = r.data || {};
        return {
          id: String(r.key || r.id || ''),
          date: String(d.CreatedDate__c || d.created_date || ''),
          field: String(d.Field || d.field || ''),
          user: String(d.CreatedById__c || d.created_by_id || ''),
          oldValue: String(d.OldValue || d.old_value || '—'),
          newValue: String(d.NewValue || d.new_value || '—'),
        };
      });
    },
    enabled: shouldUseGobodo() && (hasNumericTraversalStart || Boolean(traversalStartCaseKey)),
    staleTime: 2 * 60_000,
  });

  // Fetch CaseComments via CommentsApi (uses numeric Gobodo object ID)
  const { data: caseNumericId = parsedCaseNumericId } = useQuery({
    queryKey: ['credit-block', 'case-numeric-id', traversalStartCaseKey, parsedCaseNumericId],
    queryFn: async () => {
      if (parsedCaseNumericId > 0) return parsedCaseNumericId;
      if (!traversalStartCaseKey) return 0;
      const apiClient = getGobodoClient();
      const objectsApi = new GobodoApi.GraphObjectsApi(apiClient);
      const obj = await objectsApi.getObjectByKey(traversalStartCaseKey);
      const numeric = Number((obj as { id?: unknown })?.id ?? 0);
      return Number.isFinite(numeric) ? numeric : 0;
    },
    enabled: shouldUseGobodo() && (parsedCaseNumericId > 0 || Boolean(traversalStartCaseKey)),
    staleTime: 5 * 60 * 1000,
  });
  const { data: gobodoCaseComments = [] } = useQuery({
    queryKey: ['credit-block', 'case-comments', caseNumericId],
    queryFn: async () => {
      const apiClient = getGobodoClient();
      const commentsApi = new GobodoApi.CommentsApi(apiClient);
      const comments = await commentsApi.getComments(caseNumericId);
      if (!Array.isArray(comments)) return [];
      return comments.map((c) => ({
        id: String(c.id || ''),
        body: String(c.body || c.content || ''),
        createdBy: String(c.creatorid || ''),
        createdDate: String(c.creationdate || ''),
      }));
    },
    enabled: shouldUseGobodo() && !isNaN(caseNumericId) && caseNumericId > 0,
    staleTime: 2 * 60_000,
  });

  // Fetch EmailMessages via traversal (Case → EmailMessage)
  const { data: gobodoEmails = [] } = useQuery({
    queryKey: ['credit-block', 'case-emails', parsedCaseNumericId || traversalStartCaseKey],
    queryFn: async () => {
      const apiClient = getGobodoClient();
      const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);
      const input = new GobodoApi.GraphTraversalInput();
      input.setGraphId(GobodoConfig.graph());
      if (hasNumericTraversalStart) input.setStartObjectId(parsedCaseNumericId);
      else input.setStartObjectKey(traversalStartCaseKey);
      input.setRelationshipTypes([`case_email_rel${suffix}`]);
      input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
      input.setMaxPathLength(1);
      const result = await retryWithBackoff(() => traversalApi.traverseObjects(input), { maxRetries: 2, initialDelayMs: 1000 });
      if (!Array.isArray(result)) return [];
      return result.map((r) => {
        const d = r.data || {};
        return {
          id: String(r.key || r.id || ''),
          subject: String(d.Subject || d.subject || r.label || ''),
          fromAddress: String(d.FromAddress || d.from_address || ''),
          toAddress: String(d.ToAddress || d.to_address || ''),
          messageDate: String(d.MessageDate || d.message_date || d.CreatedDate__c || ''),
          textBody: String(d.TextBody || d.text_body || '').slice(0, 200),
        };
      });
    },
    enabled: shouldUseGobodo() && (hasNumericTraversalStart || Boolean(traversalStartCaseKey)),
    staleTime: 2 * 60_000,
  });
  const unlockRequestCaseLookup = String(traversalStartCaseKey || c.id || caseRecord.id || '').trim();
  const { data: caseUnlockRequests = [] } = useQuery({
    queryKey: ['credit-block', 'case-unlock-requests', unlockRequestCaseLookup],
    queryFn: () => fetchUnlockRequestsLinkedToCase(unlockRequestCaseLookup),
    enabled: shouldUseGobodo() && Boolean(unlockRequestCaseLookup),
    staleTime: 60_000,
  });

  const subject = String(c.Subject__c ?? c.Subject ?? c.subject ?? '');
  const numericCaseObjectId = String(
    c.numericObjectId ??
      caseRecord.numericObjectId ??
      (parsedCaseNumericId > 0 ? parsedCaseNumericId : '')
  ).trim();
  const caseNumber = String(c.caseNumber ?? c.CaseNumber__c ?? c.CaseNumber ?? '').trim() || numericCaseObjectId;
  const status = String(c.Status__c ?? c.Status ?? c.status ?? '');
  const priority = String(c.Priority__c ?? c.Priority ?? c.priority ?? '');
  const caseType = String(c.Type__c ?? c.Type ?? c.type ?? '');
  const subType = String(c.Sub_Type__c ?? c.SubType__c ?? c.subType ?? '');
  const origin = String(c.Origin__c ?? c.Origin ?? c.origin ?? c.CaseOrigin ?? c.caseOrigin ?? '');
  const displayOrigin = normalizeCreditBlockCaseOrigin(origin);
  const owner = String(c.owner ?? c.OwnerId ?? c.OwnerId__c ?? c.Owner__c ?? c.Owner ?? '');
  const description = String(c.Description__c ?? c.Description ?? c.description ?? '');
  const internalComments = String(
    c.internalComments ??
      c.Comments ??
      c.Internal_Comments__c ??
      c.InternalComments__c ??
      c.InternalComments ??
      ''
  );
  const contactName = String(
    c.contactName ??
      c.contact_name ??
      (suffix ? (c as Record<string, unknown>)[`ContactName${suffix}`] : undefined) ??
      (suffix ? (c as Record<string, unknown>)[`contact_name${suffix}`] : undefined) ??
      c.ContactName__c ??
      c.ContactName ??
      c.ContactId ??
      c.ContactId__c ??
      ''
  );
  const parentCase = String(c.parentCase ?? c.ParentCase__c ?? c.ParentCaseId__c ?? c.ParentId ?? '');
  const [parentCaseDisplayLabel, setParentCaseDisplayLabel] = useState(parentCase);
  const reblockDate = String(c.reblockDate ?? c.Reblock_Date__c ?? c.ReblockDate__c ?? '');
  const createdDate = String(c.createdDate ?? c.CreatedDate__c ?? c.CreatedDate ?? '');
  const lastModifiedDate = String(c.lastModifiedDate ?? c.LastModifiedDate__c ?? c.LastModifiedDate ?? '');
  const createdBy = String(c.createdBy ?? c.CreatedBy__c ?? c.CreatedBy ?? '');
  const lastModifiedBy = String(c.lastModifiedBy ?? c.LastModifiedBy__c ?? c.LastModifiedBy ?? '');
  const closedDate = String(c.closedDate ?? c.ClosedDate__c ?? c.DateTimeClosed__c ?? c.DateTimeClosed ?? '');
  const resolvedCaseRecordId = String(c.key ?? c.id ?? caseRecord.key ?? caseRecord.id ?? '').trim();
  const normalizeText = (v: unknown): string => String(v ?? '').trim();
  const extractStatusFromAnyRecord = (row: Record<string, unknown> | null | undefined): {
    resolved: string;
    rawStatus: string;
    rawCanonical: string;
    rawSuffixed: string;
  } => {
    const rawStatus = String(row?.status ?? '').trim();
    const rawCanonical = String(row?.Status__c ?? row?.Status ?? '').trim();
    const rawSuffixed = String(row?.[`Status${suffix}`] ?? '').trim();
    const resolved = rawSuffixed || rawCanonical || rawStatus;
    return { resolved, rawStatus, rawCanonical, rawSuffixed };
  };
  const caseRecordUpdateTargets = useMemo(
    () =>
      Array.from(
        new Set(
          [
            resolvedCaseRecordId,
            normalizeText((c as Record<string, unknown>).numericObjectId),
            normalizeText(c.id),
            normalizeText(c.key),
            normalizeText((c as Record<string, unknown>).sf_id),
            normalizeText((c as Record<string, unknown>).salesforce_id),
          ].filter(Boolean)
        )
      ),
    [resolvedCaseRecordId, c]
  );
  const readCaseStatusFromServer = async () => {
    for (const candidateId of caseRecordUpdateTargets) {
      try {
        const record = /^\d+$/.test(candidateId)
          ? await getGobodoRecordById(candidateId)
          : await getGobodoRecordByKey(candidateId);
        if (!record) continue;
        return extractStatusFromAnyRecord(record as Record<string, unknown>);
      } catch {
        continue;
      }
    }
    return null;
  };
  const readCaseRecordFromServer = async (): Promise<Record<string, unknown> | null> => {
    for (const candidateId of caseRecordUpdateTargets) {
      try {
        const record = /^\d+$/.test(candidateId)
          ? await getGobodoRecordById(candidateId)
          : await getGobodoRecordByKey(candidateId);
        if (!record) continue;
        return record as Record<string, unknown>;
      } catch {
        continue;
      }
    }
    return null;
  };
  const refreshCaseLocalFromServer = async () => {
    const serverRecord = await readCaseRecordFromServer();
    if (!serverRecord) return;
    const serverStatus = String(
      serverRecord[`Status${suffix}`] ?? serverRecord.Status__c ?? serverRecord.Status ?? serverRecord.status ?? ''
    ).trim();
    const serverOwnerValue = String(
      serverRecord[`OwnerRelField${suffix}`] ??
        serverRecord.OwnerRelField__c ??
        serverRecord.OwnerId__c ??
        serverRecord.OwnerId ??
        serverRecord.owner_id ??
        ''
    ).trim();
    const serverOwnerLabel = String(
      serverRecord.Owner__c ??
        serverRecord.OwnerName__c ??
        serverRecord.OwnerName ??
        serverRecord.owner_name ??
        serverRecord.ownerName ??
        serverRecord.owner ??
        ''
    ).trim();
    if (serverStatus) {
      setLocalCaseValues((prev) => ({ ...prev, status: serverStatus }));
    }
    if (serverOwnerValue) {
      setLocalCaseValues((prev) => ({ ...prev, owner: serverOwnerValue }));
      const resolvedLabel = serverOwnerLabel || ownerLabelByValue.get(serverOwnerValue) || '';
      if (resolvedLabel) setLocalOwnerLabel(resolvedLabel);
    } else if (serverOwnerLabel) {
      setLocalOwnerLabel(serverOwnerLabel);
    }
  };
  /** Default `gobodoPropertyPatch: true` (disk / workflow steps). Pass `{ gobodoPropertyPatch: false }` for full modal save. */
  const updateCaseRecord = async (
    payload: Record<string, string | null>,
    opts?: { gobodoPropertyPatch?: boolean }
  ) => {
    const gobodoPropertyPatch = opts?.gobodoPropertyPatch !== false;
    let lastError: unknown = null;
    for (const candidateId of caseRecordUpdateTargets) {
      try {
        return await updateRecord(`Case${suffix}`, candidateId, payload, { gobodoPropertyPatch });
      } catch (error) {
        lastError = error;
      }
    }
    throw (lastError instanceof Error
      ? lastError
      : new Error('Failed to update case: could not resolve a valid case record id/key.'));
  };
  const ownerLookupValue = String(
    c.OwnerId ??
      c.OwnerId__c ??
      c[`OwnerRelField${suffix}`] ??
      c.OwnerRelField__c ??
      c.owner_id ??
      c.ownerSfId ??
      owner ??
      ''
  ).trim();
  const { data: ownerLookupResolved } = useQuery({
    queryKey: ['credit-block', 'case-detail', 'owner-lookup-resolve', ownerLookupValue],
    queryFn: async () => findGraphUserBySfId(ownerLookupValue),
    enabled: shouldUseGobodo() && !!ownerLookupValue && looksLikeGraphUserSfId(ownerLookupValue),
    staleTime: 5 * 60 * 1000,
  });
  const resolvedOwnerLookupValue = String(
    ownerLookupResolved?.key ?? ownerLookupResolved?.id ?? ownerLookupValue
  ).trim();
  const updateCaseField = useMutation({
    mutationFn: async (payload: Record<string, string | null>) => {
      if (!resolvedCaseRecordId) throw new Error('Missing case id');
      return updateCaseRecord(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'case-detail-owner'] });
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'cases'] });
      if (resolvedCaseRecordId) {
        queryClient.invalidateQueries({ queryKey: ['credit-block', 'case-by-id', resolvedCaseRecordId] });
      }
    },
    onError: (error) => {
      setWorkflowError(error instanceof Error ? error.message : 'Failed to save case field.');
    },
  });

  // Resolve display names — prefer explicit *_name fields, then safe non-ID fallbacks.
  const isPlaceholderText = (v: unknown): boolean => {
    const s = String(v ?? '').trim().toLowerCase();
    return !s || s === '-1' || s === '0' || s === 'null' || s === 'undefined';
  };
  const pickFirstText = (...vals: unknown[]): string => {
    for (const v of vals) {
      const s = String(v ?? '').trim();
      if (!isPlaceholderText(s)) return s;
    }
    return '';
  };
  const looksLikeRecordId = (v: string): boolean => {
    const s = String(v ?? '').trim();
    if (!s) return false;
    if (/^-?\d+$/.test(s)) return true;
    if (/^[a-zA-Z0-9]{15,18}$/.test(s) && /^0[0-9A-Za-z]{2}/.test(s)) return true;
    return s.length > 20 && /^[a-zA-Z0-9_-]+$/.test(s);
  };
  const createdByName = pickFirstText(
    c.created_by_name__stg3_c,
    c.created_by_name,
    c.CreatedByName__c,
    c.CreatedByName,
    c.createdByName
  );
  const lastModifiedByName = pickFirstText(
    c.last_modified_by_name__stg3_c,
    c.last_modified_by_name,
    c.LastModifiedByName__c,
    c.LastModifiedByName,
    c.lastModifiedByName
  );
  const ownerName = pickFirstText(
    c.owner_name__stg3_c,
    c.owner_name,
    c.AccountOwner__c,
    c.OwnerName__c,
    c.OwnerName,
    c.ownerName
  );
  const displayCreatedBy = createdByName || (!isPlaceholderText(createdBy) && !looksLikeRecordId(createdBy) ? createdBy : 'System');
  const displayLastModifiedBy =
    lastModifiedByName || (!isPlaceholderText(lastModifiedBy) && !looksLikeRecordId(lastModifiedBy) ? lastModifiedBy : displayCreatedBy);
  const displayOwner = ownerName || (owner && !looksLikeRecordId(owner) ? owner : '');
  const accountNameResolved = pickFirstText(
    clientName,
    c.accountName,
    c.AccountName__c,
    c.AccountName,
    c.account_name__stg3_c,
    c.account_name,
    c.ClientName__c,
    c.clientName,
    c.relatedClientName,
    c.relatedClientLabel,
    linkedClientInfo?.displayName
  );
  const accountLookupForNavigation = pickFirstText(
    linkedClientInfo?.clientKey,
    linkedClientInfo?.clientNumericId,
    accountLookup
  );
  const canOpenAccountClient = Boolean(onOpenClientDetails && accountLookupForNavigation);
  const linkedClientForBureaus = pickFirstText(linkedClientInfo?.clientKey, linkedClientInfo?.clientNumericId, accountLookup);
  const { data: linkedClientBureaus } = useCreditBlockBureaus(
    linkedClientForBureaus,
    Boolean(shouldUseGobodo() && linkedClientForBureaus)
  );

  const workflowTags = useMemo(() => {
    const commentsText = String(localCaseValues.internalComments ?? '').toUpperCase();
    return {
      autoConfirmVerified: commentsText.includes('[AUTO_CONFIRM_VERIFIED]'),
      slackScreenshotSent: commentsText.includes('[SLACK_SS_SENT]'),
      manualConfirmSent: commentsText.includes('[MANUAL_CONFIRM_SENT]'),
    };
  }, [localCaseValues.internalComments]);

  const toText = (v: unknown): string => String(v ?? '').trim();
  const normalizeRequestType = (row: Record<string, unknown>): string => {
    const d = (row.data as Record<string, unknown>) ?? row;
    return String(
      row.Request_Type__c ??
        d.Request_Type__c ??
        row.Type__c ??
        d.Type__c ??
        row.type ??
        d.type ??
        row.custom__request_type ??
        d.custom__request_type ??
        ''
    )
      .toLowerCase()
      .trim();
  };
  const normalizeRequestStatus = (row: Record<string, unknown>): string => {
    const d = (row.data as Record<string, unknown>) ?? row;
    return String(
      row.Status__c ??
        d.Status__c ??
        row.status ??
        d.status ??
        row.custom__status ??
        d.custom__status ??
        ''
    )
      .toLowerCase()
      .trim();
  };
  const normalizeRequestBureau = (row: Record<string, unknown>): 'equifax' | 'experian' | 'transunion' | 'chexsystems' | 'all' | '' => {
    const d = (row.data as Record<string, unknown>) ?? row;
    const raw = String(
      row.Bureau_Name__c ??
        d.Bureau_Name__c ??
        row.Bureau__c ??
        d.Bureau__c ??
        row.bureau ??
        d.bureau ??
        row.custom__bureau ??
        d.custom__bureau ??
        row.custom__bureau_new ??
        d.custom__bureau_new ??
        ''
    )
      .toLowerCase()
      .trim();
    if (!raw) return '';
    if (raw.includes('all')) return 'all';
    if (raw.includes('equifax')) return 'equifax';
    if (raw.includes('experian')) return 'experian';
    if (raw.includes('transunion') || raw.includes('trans union')) return 'transunion';
    if (raw.includes('chex')) return 'chexsystems';
    return '';
  };
  const linkedUnblockRequests = useMemo(() => {
    const rows = Array.isArray(caseUnlockRequests) ? caseUnlockRequests : [];
    return rows.filter((r) => {
      const type = normalizeRequestType(r as Record<string, unknown>);
      return (
        type.includes('unlock') ||
        type.includes('unblock') ||
        type.includes('extension')
      );
    });
  }, [caseUnlockRequests]);
  const linkedUnblockRequestsAllCompleted = useMemo(() => {
    if (linkedUnblockRequests.length === 0) return false;
    return linkedUnblockRequests.every((r) => {
      const statusVal = normalizeRequestStatus(r as Record<string, unknown>);
      return statusVal === 'completed' || statusVal === 'approved' || statusVal === 'resolved';
    });
  }, [linkedUnblockRequests]);
  const requestedBureaus = useMemo(() => {
    const out = new Set<string>();
    linkedUnblockRequests.forEach((row) => {
      const b = normalizeRequestBureau(row as Record<string, unknown>);
      if (!b) return;
      if (b === 'all') {
        out.add('equifax');
        out.add('experian');
        out.add('transunion');
      } else {
        out.add(b);
      }
    });
    return Array.from(out);
  }, [linkedUnblockRequests]);
  const linkedBureausAllUnblocked = useMemo(() => {
    if (requestedBureaus.length === 0) return false;
    return requestedBureaus.every((bureau) => {
      const statusVal = String((linkedClientBureaus as Record<string, unknown>)?.[bureau]?.status ?? '')
        .toLowerCase()
        .trim();
      return statusVal === 'unlocked';
    });
  }, [linkedClientBureaus, requestedBureaus]);
  const requestorEmails = useMemo(() => {
    const emails = new Set<string>();
    const rows = Array.isArray(caseUnlockRequests) ? caseUnlockRequests : [];
    const collect = (v: unknown) => {
      const s = String(v ?? '').trim();
      if (!s) return;
      const parts = s.split(/[,\s;]+/).filter(Boolean);
      for (const p of parts) {
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p)) emails.add(p.toLowerCase());
      }
    };
    rows.forEach((row) => {
      const d = (row as Record<string, unknown>).data as Record<string, unknown> | undefined;
      collect((row as Record<string, unknown>).Requested_By__c);
      collect(d?.Requested_By__c);
      collect((row as Record<string, unknown>).requestedBy);
      collect(d?.requestedBy);
      collect((row as Record<string, unknown>).requestorEmail);
      collect(d?.requestorEmail);
      collect((row as Record<string, unknown>).Requestor_Email__c);
      collect(d?.Requestor_Email__c);
    });
    return Array.from(emails);
  }, [caseUnlockRequests]);

  useEffect(() => {
    const seededTo = requestorEmails.join(', ');
    if (seededTo && !manualEmailTo.trim()) setManualEmailTo(seededTo);
    if (!manualEmailSubject.trim()) {
      setManualEmailSubject(`Unblock confirmation — Case ${caseNumber || subject || ''}`.trim());
    }
    if (!manualEmailBody.trim()) {
      const lines = [
        `Case: ${caseNumber || 'N/A'}`,
        `Client: ${accountNameResolved || clientName || 'N/A'}`,
        '',
        'The unblock request has been processed and completed.',
        '',
        'Please review the attached/recorded details in Salesforce case comments for bureau and timeframe confirmation.',
      ];
      setManualEmailBody(lines.join('\n'));
    }
  }, [requestorEmails, manualEmailTo, manualEmailSubject, manualEmailBody, caseNumber, subject, accountNameResolved, clientName]);

  const appendInternalWorkflowComment = async (token: string, note: string) => {
    if (!resolvedCaseRecordId) return;
    const current = toText(localCaseValues.internalComments);
    const stamp = new Date().toISOString();
    const line = `${token} ${stamp} ${note}`.trim();
    const next = current ? `${current}\n${line}` : line;
    await updateCaseRecord({
      Comments: next,
      [`Internal_Comments${suffix}`]: next,
    });
    setLocalCaseValues((prev) => ({ ...prev, internalComments: next }));
    queryClient.invalidateQueries({ queryKey: ['credit-block', 'cases'] });
  };

  const updateCaseStatusWorkflow = async (nextStatus: string) => {
    if (!resolvedCaseRecordId) throw new Error('Missing case id.');
    const payload: Record<string, string | null> = {
      Status: nextStatus,
      Status__c: nextStatus,
      [`Status${suffix}`]: nextStatus,
      status: nextStatus,
    };
    await updateCaseRecord(payload);
    if (nextStatus.toLowerCase() === 'resolved' || nextStatus.toLowerCase() === 'closed') {
      const nowIso = new Date().toISOString();
      try {
        await updateCaseRecord({
          ClosedDate: nowIso,
          ClosedDate__c: nowIso,
          DateTimeClosed__c: nowIso,
          DateTimeClosed: nowIso,
        });
      } catch {
        // Some environments do not expose all close-date fields; status update already succeeded.
      }
    }
    setLocalCaseValues((prev) => ({ ...prev, status: nextStatus }));
    lastLocalStatusSetRef.current = Date.now();
    await refreshCaseLocalFromServer().catch(() => {});
    queryClient.invalidateQueries({ queryKey: ['credit-block', 'cases'] });
  };

  const moveCaseToInProgress = async () => {
    setWorkflowError(null);
    setWorkflowNotice(null);
    const currentStatus = String(localCaseValues.status ?? '').toLowerCase().trim();
    if (!currentStatus || currentStatus === 'new' || currentStatus === 'assigned') {
      setWorkflowBusy(true);
      try {
        const previousStatusLabel = localCaseValues.status || 'New';
        await updateCaseStatusWorkflow('In Progress');
        await appendInternalWorkflowComment(
          '[PROCESSING_STARTED]',
          `Processor changed status ${previousStatusLabel} → In Progress.`
        );
        if (recordIdForActivity) {
          // Don't swallow — surfacing the error keeps this consistent with the
          // inline pencil-edit path, and the new dev-mode raw-error logging in
          // gobodoActivityProvider will explain what's actually rejected.
          try {
            await createActivity({
              type: 'note',
              recordId: recordIdForActivity,
              recordType: 'case',
              userId: String(c.ownerId ?? c.OwnerId__c ?? c.CreatedById__c ?? 'system'),
              userName: displayOwner || 'System',
              subject: 'Status changed to In Progress',
              description: `Processor changed case status from ${previousStatusLabel} to In Progress.`,
              source: 'workflow',
              sourceApp: 'credit_block',
            });
            await refetchCaseActivities?.();
          } catch (activityError) {
            // Status update already succeeded; just warn about the missing log.
            if (process.env.NODE_ENV === 'development') {
              console.warn('[CaseDetailView] In Progress activity log failed:', activityError);
            }
            setWorkflowError(
              activityError instanceof Error
                ? `Status updated but activity log failed: ${activityError.message}`
                : 'Status updated but activity log failed.',
            );
          }
        }
        setWorkflowNotice('Status updated to In Progress.');
      } catch (error) {
        setWorkflowError(error instanceof Error ? error.message : 'Unable to set In Progress.');
      } finally {
        setWorkflowBusy(false);
      }
      return;
    }
    setWorkflowNotice(`In Progress is only available from New or Assigned (current: "${localCaseValues.status || 'Unknown'}").`);
  };

  const resolveUnlockCase = async () => {
    setWorkflowError(null);
    setWorkflowNotice(null);
    if (!linkedUnblockRequestsAllCompleted) {
      setWorkflowError('Cannot resolve yet: linked unblock requests are not all Completed/Approved.');
      return;
    }
    if (!linkedBureausAllUnblocked) {
      setWorkflowError('Cannot resolve yet: not all requested bureaus are in Unblocked stage status.');
      return;
    }
    setWorkflowBusy(true);
    try {
      const requestRows = Array.isArray(linkedUnblockRequests) ? linkedUnblockRequests : [];
      await Promise.allSettled(
        requestRows.map((row) => {
          const id = toText((row as Record<string, unknown>).id ?? (row as Record<string, unknown>).key);
          if (!id) return Promise.resolve(null);
          const statusVal = normalizeRequestStatus(row as Record<string, unknown>);
          if (statusVal === 'completed') return Promise.resolve(null);
          return updateRecord(
            `Unlock_Request${suffix}`,
            id,
            {
              Status__c: 'Completed',
              status: 'completed',
            },
            { gobodoPropertyPatch: true }
          );
        })
      );
      await updateCaseStatusWorkflow('Resolved');
      const autoConfirmSubject = `Automatic unblock confirmation — Case ${caseNumber || ''}`.trim();
      const autoConfirmBodyLines = [
        `Case: ${caseNumber || 'N/A'}`,
        `Client: ${accountNameResolved || clientName || 'N/A'}`,
        '',
        'Your unblock request has been completed.',
        'This automatic confirmation is sent after all requested bureaus are updated to Unblocked status.',
      ];
      const autoConfirmBody = autoConfirmBodyLines.join('\n');
      if (requestorEmails.length > 0) {
        await sendEmailMutation.mutateAsync({
          to: requestorEmails,
          subject: autoConfirmSubject,
          body: autoConfirmBody,
        });
      }
      await appendInternalWorkflowComment(
        '[AUTO_CONFIRM_VERIFIED]',
        requestorEmails.length > 0
          ? `All requested bureaus are unblocked. Case marked as Resolved and automatic unblock confirmation sent to: ${requestorEmails.join(', ')}.`
          : 'All requested bureaus are unblocked. Case marked as Resolved; no requestor email was found for automatic confirmation.'
      );
      if (recordIdForActivity) {
        await createActivity({
          type: 'note',
          recordId: recordIdForActivity,
          recordType: 'case',
          userId: String(c.ownerId ?? c.OwnerId__c ?? c.CreatedById__c ?? c.createdBy ?? 'system'),
          userName: displayOwner || displayLastModifiedBy || displayCreatedBy || 'System',
          subject: `Automatic unblock confirmation — ${caseNumber || 'Case'}`,
          description: 'Automatic unblock confirmation verified after all requested bureaus updated to Unblocked.',
          source: 'workflow',
          sourceApp: 'credit_block',
        });
        await refetchCaseActivities();
      }
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'case-unlock-requests'] });
      setWorkflowNotice('Case marked as Resolved. Automatic unblock confirmation verification logged.');
    } catch (error) {
      setWorkflowError(error instanceof Error ? error.message : 'Unable to resolve case.');
    } finally {
      setWorkflowBusy(false);
    }
  };

  const markSlackScreenshotSent = async () => {
    setWorkflowError(null);
    setWorkflowNotice(null);
    setWorkflowBusy(true);
    try {
      await appendInternalWorkflowComment('[SLACK_SS_SENT]', 'Processor sent Stage Status and closed-case screenshot to Slack group chat.');
      setWorkflowNotice('Slack screenshot confirmation logged.');
    } catch (error) {
      setWorkflowError(error instanceof Error ? error.message : 'Unable to log Slack confirmation.');
    } finally {
      setWorkflowBusy(false);
    }
  };

  const sendManualConfirmationEmail = async () => {
    setWorkflowError(null);
    setWorkflowNotice(null);
    const parseList = (raw: string): string[] =>
      String(raw ?? '')
        .split(/[,\s;]+/)
        .map((v) => v.trim().toLowerCase())
        .filter((v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v));
    const to = parseList(manualEmailTo);
    const cc = parseList(manualEmailCc);
    if (to.length === 0) {
      setWorkflowError('Manual email requires at least one valid recipient in To.');
      return;
    }
    if (!toText(manualEmailSubject) || !toText(manualEmailBody)) {
      setWorkflowError('Manual email requires subject and body.');
      return;
    }
    setWorkflowBusy(true);
    try {
      await sendEmailMutation.mutateAsync({
        to,
        cc: cc.length > 0 ? cc : undefined,
        subject: manualEmailSubject,
        body: manualEmailBody,
      });
      await appendInternalWorkflowComment(
        '[MANUAL_CONFIRM_SENT]',
        `Manual unblock confirmation email sent. To: ${to.join(', ')}${cc.length ? ` | Cc: ${cc.join(', ')}` : ''}.`
      );
      if (recordIdForActivity) {
        await createActivity({
          type: 'email',
          recordId: recordIdForActivity,
          recordType: 'case',
          userId: String(c.ownerId ?? c.OwnerId__c ?? c.CreatedById__c ?? c.createdBy ?? 'system'),
          userName: displayOwner || displayLastModifiedBy || displayCreatedBy || 'System',
          subject: manualEmailSubject,
          description: manualEmailBody.slice(0, 1000),
          source: 'manual',
          sourceApp: 'credit_block',
        });
        await refetchCaseActivities();
      }
      setWorkflowNotice('Manual unblock confirmation email sent and logged.');
    } catch (error) {
      setWorkflowError(error instanceof Error ? error.message : 'Failed to send manual confirmation email.');
    } finally {
      setWorkflowBusy(false);
    }
  };

  const submitCaseComment = async () => {
    const body = newCommentBody.trim();
    if (!body) return;
    if (!caseNumericId || caseNumericId <= 0) {
      setWorkflowError('Cannot add comment: case numeric ID not resolved.');
      return;
    }
    setCommentSubmitting(true);
    try {
      const apiClient = getGobodoClient();
      const commentsApi = new GobodoApi.CommentsApi(apiClient);
      const input = new GobodoApi.CommentInput({});
      input.setParentobjectid(caseNumericId);
      input.setBody(body);
      input.setSubject('Case Comment');
      await commentsApi.createComment(input);
      setNewCommentBody('');
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'case-comments', caseNumericId] });
    } catch (error) {
      setWorkflowError(error instanceof Error ? error.message : 'Failed to add comment.');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const closeCaseWithComment = async () => {
    setWorkflowError(null);
    setWorkflowNotice(null);
    const closingComment = newCommentBody.trim();
    setWorkflowBusy(true);
    try {
      const postCloseIssues: string[] = [];
      // Add the closing comment via CommentsApi
      if (closingComment && caseNumericId && caseNumericId > 0) {
        const apiClient = getGobodoClient();
        const commentsApi = new GobodoApi.CommentsApi(apiClient);
        const input = new GobodoApi.CommentInput({});
        input.setParentobjectid(caseNumericId);
        input.setBody(closingComment);
        input.setSubject('Case Closed');
        try {
          await commentsApi.createComment(input);
          queryClient.invalidateQueries({ queryKey: ['credit-block', 'case-comments', caseNumericId] });
        } catch {
          postCloseIssues.push('Closing comment could not be posted.');
        }
      }
      // Update status to Closed
      await updateCaseStatusWorkflow('Closed');
      // Non-blocking post-close actions
      try {
        await appendInternalWorkflowComment('[CASE_CLOSED]', closingComment || 'Closed with existing case comments.');
      } catch {
        postCloseIssues.push('Internal close note failed.');
      }
      if (requestorEmails.length > 0) {
        const confirmSubject = `Case Closed — ${caseNumber || subject || ''}`.trim();
        const confirmBody = [
          `Case: ${caseNumber || 'N/A'}`,
          `Client: ${accountNameResolved || clientName || 'N/A'}`,
          '',
          'This case has been closed.',
          closingComment ? `Closing notes: ${closingComment}` : '',
          '',
          'If you have any questions, please reply to this email.',
        ].filter(Boolean).join('\n');
        try {
          await sendEmailMutation.mutateAsync({
            to: requestorEmails,
            subject: confirmSubject,
            body: confirmBody,
          });
        } catch {
          postCloseIssues.push('Confirmation email failed.');
        }
      }
      // Log close as activity
      if (recordIdForActivity) {
        await createActivity({
          type: 'note',
          recordId: recordIdForActivity,
          recordType: 'case',
          userId: String(c.ownerId ?? c.OwnerId__c ?? c.CreatedById__c ?? 'system'),
          userName: displayOwner || 'System',
          subject: 'Case Closed',
          description: closingComment || 'Case closed.',
          source: 'workflow',
          sourceApp: 'credit_block',
        }).catch(() => {});
        refetchCaseActivities?.();
      }
      setNewCommentBody('');
      setCloseCaseCommentRequired(false);
      setWorkflowNotice(
        postCloseIssues.length > 0
          ? `Case closed with warnings: ${postCloseIssues.join(' ')}`
          : 'Case closed successfully.'
      );
    } catch (error) {
      setWorkflowError(error instanceof Error ? error.message : 'Failed to close case.');
    } finally {
      setWorkflowBusy(false);
    }
  };

  const caseIdentity = String(caseRecord.key ?? caseRecord.id ?? '').trim();
  const lastIncomingStatusRef = useRef<string>('');
  const lastLocalStatusSetRef = useRef<number>(0);
  const lastLocalOwnerSetRef = useRef<number>(0);
  useEffect(() => {
    setLocalCaseValues({
      subject,
      owner: resolvedOwnerLookupValue,
      status,
      caseType,
      origin: displayOrigin,
      subType,
      priority,
      reblockDate,
      description,
      internalComments,
      contactName,
      parentCase,
    });
    setParentCaseDisplayLabel(parentCase);
    setLocalOwnerLabel(displayOwner || ownerName || owner || '');
    lastIncomingStatusRef.current = String(status ?? '').trim();
  }, [caseIdentity]);
  useEffect(() => {
    const incomingStatus = String(status ?? '').trim();
    if (!incomingStatus) return;
    if (incomingStatus === lastIncomingStatusRef.current) return;
    if (Date.now() - lastLocalStatusSetRef.current < 5000) return;
    lastIncomingStatusRef.current = incomingStatus;
    setLocalCaseValues((prev) => {
      const current = String(prev.status ?? '').trim();
      if (current === incomingStatus) return prev;
      return { ...prev, status: incomingStatus };
    });
  }, [status, caseIdentity]);
  useEffect(() => {
    if (editingField === 'owner') return;
    if (Date.now() - lastLocalOwnerSetRef.current < 10000) return;
    const incomingOwner = String(resolvedOwnerLookupValue ?? '').trim();
    if (!incomingOwner) return;
    const incomingLabel = String(
      ownerLookupResolved?.displayName ?? displayOwner ?? ownerName ?? owner ?? ''
    ).trim();
    setLocalCaseValues((prev) => {
      const current = String(prev.owner ?? '').trim();
      if (!current) return { ...prev, owner: incomingOwner };
      if (current === incomingOwner) return prev;
      return prev;
    });
    if (incomingLabel) {
      setLocalOwnerLabel((prev) => prev || incomingLabel);
    }
  }, [resolvedOwnerLookupValue, editingField, caseIdentity]);
  /**
   * Single source of truth for both the inline "Case Owner" pencil-edit and
   * the Transfer Case modal: active users hydrated with their Credit Block
   * roles, filtered to Team Leads + Processors only. Previously two parallel
   * queries existed — one unfiltered (used by the inline picker, showed every
   * user including duplicates like "Magnus Deppe / Magnus Deppes") and one
   * filtered (used by the modal). Sharing one query halves the user-hydration
   * work and keeps both pickers consistent — owner assignment is always
   * scoped to people who actually work Credit Block cases. The fallback
   * branch covers environments where role_user__c links haven't been seeded.
   */
  const { data: cbRoleTeamUsers = [], isLoading: transferTeamUsersLoading } = useQuery({
    queryKey: CREDIT_BLOCK_TEAM_USERS_QUERY_KEY,
    queryFn: fetchCreditBlockTeamUsers,
    enabled: shouldUseGobodo(),
    staleTime: 5 * 60 * 1000,
  });
  const userRows = cbRoleTeamUsers;
  const transferTeamOptions = useMemo(() => {
    const roleByValue = new Map(
      cbRoleTeamUsers.map((user) => [
        String(user.graphObjectKey ?? user.id ?? '').trim(),
        getCreditBlockRoleLabel(user),
      ]),
    );
    return usersToCreditBlockAssigneeOptions(cbRoleTeamUsers).map((option) => {
      const value = option.value.replace(/^u:/, '');
      return {
        ...option,
        value,
        roleLabel: roleByValue.get(value) || 'Credit Block role',
      };
    });
  }, [cbRoleTeamUsers]);
  const statusOptions = useMemo(() => {
    const fromMeta = getPicklistValues('Case', 'Status') ?? [];
    if (fromMeta.length > 1) return fromMeta;
    if (fromMeta.length === 1 && fromMeta[0].toLowerCase() !== 'new') return fromMeta;
    return DEFAULT_CASE_STATUS_OPTIONS;
  }, [metaVersion]);
  const typeOptions = useMemo(() => getPicklistValues('Case', 'Type') ?? [], [metaVersion]);
  const originOptions = useMemo(() => {
    const options = (getPicklistValues('Case', 'Origin') ?? []).map(normalizeCreditBlockCaseOrigin).filter(Boolean);
    return Array.from(new Set([CREDIT_BLOCK_SYSTEM_CASE_ORIGIN, ...options]));
  }, [metaVersion]);
  const priorityOptions = useMemo(() => getPicklistValues('Case', 'Priority') ?? [], [metaVersion]);
  const subTypeOptions = useMemo(() => {
    const fromMeta = getPicklistValues('Case', `Sub_Type${suffix}`) ?? [];
    return fromMeta.length > 0 ? fromMeta : DEFAULT_CASE_SUBTYPE_OPTIONS;
  }, [metaVersion, suffix]);
  const fieldApiMap: Record<string, string[]> = {
    subject: ['Subject', 'Subject__c', `Subject${suffix}`, 'subject'],
    owner: ['OwnerId', `OwnerRelField${suffix}`],
    status: ['Status', 'Status__c', `Status${suffix}`, 'status'],
    caseType: ['Type', 'Type__c', `Type${suffix}`, 'type'],
    origin: ['Origin', 'Origin__c', `Origin${suffix}`, 'origin'],
    subType: ['Sub_Type__c', `Sub_Type${suffix}`, 'SubType__c', 'subType'],
    priority: ['Priority', 'Priority__c', `Priority${suffix}`, 'priority'],
    reblockDate: ['Reblock_Date__c', `Reblock_Date${suffix}`],
    description: ['Description', 'Description__c', `Description${suffix}`, 'description'],
    internalComments: ['Comments', `Internal_Comments${suffix}`],
    contactName: ['ContactId', `ContactName${suffix}`],
    parentCase: ['ParentId'],
  };
  const ownerLookupOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string }> = [];
    const seen = new Set<string>();
    for (const u of userRows || []) {
      const value = String(u.graphObjectKey ?? u.id ?? '').trim();
      if (!value || seen.has(value)) continue;
      seen.add(value);
      const fullName = `${String(u.firstName ?? '').trim()} ${String(u.lastName ?? '').trim()}`.trim();
      const labelBase = fullName || String(u.username ?? '').trim() || String(u.email ?? '').trim() || value;
      const secondary = String(u.username ?? '').trim() || String(u.email ?? '').trim();
      const label = secondary && secondary !== labelBase ? `${labelBase} (${secondary})` : labelBase;
      opts.push({ value, label });
    }
    const fallbackLabel = String(ownerLookupResolved?.displayName ?? displayOwner ?? owner ?? '').trim();
    const fallbackValue = String(resolvedOwnerLookupValue ?? '').trim();
    if (fallbackValue && !seen.has(fallbackValue)) {
      opts.unshift({ value: fallbackValue, label: fallbackLabel || fallbackValue });
    }
    return opts;
  }, [userRows, resolvedOwnerLookupValue, ownerLookupResolved?.displayName, displayOwner, owner]);
  const ownerLabelByValue = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of ownerLookupOptions) map.set(opt.value, opt.label);
    return map;
  }, [ownerLookupOptions]);
  const ownerLookupField = useMemo(() => ({
    id: 'case-owner-lookup',
    objectId: `Case${suffix}`,
    fieldName: `OwnerRelField${suffix}`,
    fieldLabel: 'Case Owner',
    fieldType: 'lookup',
    required: false,
    unique: false,
    lookupObject: 'User',
    isPII: false,
    isStandard: false,
    isActive: true,
    fieldLevelSecurity: {
      visibleTo: [],
      editableBy: [],
    },
    createdBy: 'system',
    createdDate: '',
    lastModified: '',
  }), [suffix]);
  const parentCaseLookupField = useMemo(() => ({
    id: 'case-parent-case-lookup',
    objectId: `Case${suffix}`,
    fieldName: 'ParentId',
    fieldLabel: 'Parent Case',
    fieldType: 'lookup',
    required: false,
    unique: false,
    lookupObject: `Case${suffix}`,
    isPII: false,
    isStandard: false,
    isActive: true,
    fieldLevelSecurity: {
      visibleTo: [],
      editableBy: [],
    },
    createdBy: 'system',
    createdDate: '',
    lastModified: '',
  }), [suffix]);
  const buildOwnerUpdatePayload = (ownerValue: string, ownerLabel?: string): Record<string, string | null> => {
    const normalizedValue = String(ownerValue ?? '').trim();
    const ownerRelValue = isNameOwnerToken(normalizedValue) ? '' : normalizedValue;
    const normalizedLabel = String(
      ownerLabel ??
        ownerLabelByValue.get(normalizedValue) ??
        displayOwner ??
        owner ??
        ''
    ).trim();
    const normalizedCurrentStatus = String(localCaseValues.status ?? '').trim().toLowerCase();
    const shouldSetAssignedStatus = Boolean(normalizedValue) && normalizedCurrentStatus !== 'closed';
    return {
      Owner__c: normalizedLabel || null,
      owner: normalizedLabel || null,
      [`OwnerRelField${suffix}`]: ownerRelValue || null,
      ...(shouldSetAssignedStatus
        ? {
            Status: 'Assigned',
            Status__c: 'Assigned',
            [`Status${suffix}`]: 'Assigned',
            status: 'Assigned',
          }
        : {}),
    };
  };
  const startEdit = (fieldKey: string, value: string) => {
    setEditingField(fieldKey);
    setEditValue(value ?? '');
    setEditLookupLabel(
      fieldKey === 'owner'
        ? localOwnerLabel || ownerLabelByValue.get(String(value ?? '').trim()) || ownerLookupResolved?.displayName || displayOwner || owner || ''
        : ''
    );
  };
  // Transfer Case modal handlers
  const openTransferModal = () => {
    setTransferModalOpen(true);
  };
  const closeTransferModal = () => {
    setTransferModalOpen(false);
    setTransferSaving(false);
  };
  const handleTransferSelect = async (assignee: { value: string; label: string }) => {
    setTransferSaving(true);
    try {
      const oldOwnerLabel = String(
        localOwnerLabel ||
          ownerLabelByValue.get(String(localCaseValues.owner ?? '').trim()) ||
          displayOwner ||
          '',
      ).trim();
      const payload = buildOwnerUpdatePayload(assignee.value, assignee.label);
      await updateCaseField.mutateAsync(payload);
      setLocalCaseValues((prev) => ({
        ...prev,
        owner: assignee.value,
        status: assignee.value ? 'Assigned' : prev.status,
      }));
      setLocalOwnerLabel(assignee.label);
      // editLookupLabel feeds the lookup field's selectedOptionLabel and is
      // part of the display chain when re-entering inline edit. saveEdit
      // updates it on every owner change; mirror that here so the picker
      // and field both render the new owner without waiting for a server
      // refetch.
      setEditLookupLabel(assignee.label);
      lastLocalOwnerSetRef.current = Date.now();
      if (assignee.value) lastLocalStatusSetRef.current = Date.now();

      // Log activity to Gobodo (not localStorage) so the Activities feed
      // renders the transfer. Mirrors the saveEdit pattern.
      //
      // IMPORTANT: the activity log can fail (schema mismatch, permission,
      // etc.) but the case write has already succeeded. Don't let an
      // activity-log error block closing the modal — surface it as a notice
      // instead so the user knows the transfer landed but the chatter
      // entry didn't.
      if (recordIdForActivity && oldOwnerLabel !== assignee.label) {
        const activityPayload = {
          type: 'note' as const,
          recordId: recordIdForActivity,
          recordType: 'case' as const,
          userId: String(c.ownerId ?? c.OwnerId__c ?? c.CreatedById__c ?? 'system'),
          userName: displayOwner || displayLastModifiedBy || displayCreatedBy || 'System',
          subject: 'Case transferred',
          description: `Case transferred from "${oldOwnerLabel || '(unassigned)'}" to "${assignee.label || '(unassigned)'}"`,
          source: 'manual' as const,
          sourceApp: 'credit_block' as const,
        };
        try {
          try {
            await createActivity(activityPayload);
          } catch {
            await createActivity({
              ...activityPayload,
              userId: 'system',
              userName: displayLastModifiedBy || displayCreatedBy || 'System',
            });
          }
          await refetchCaseActivities?.();
        } catch (activityError) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[CaseDetailView] Transfer activity log failed:', activityError);
          }
          setWorkflowError(
            activityError instanceof Error
              ? `Transferred to ${assignee.label} but activity log failed: ${activityError.message}`
              : `Transferred to ${assignee.label} but activity log failed.`,
          );
        }
      }

      closeTransferModal();
    } catch (transferError) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[CaseDetailView] Transfer write failed:', transferError);
      }
      setWorkflowError(
        transferError instanceof Error ? transferError.message : 'Failed to transfer case.',
      );
      setTransferSaving(false);
    }
  };

  // Case file upload handler
  const handleCaseFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    let uploaded = 0;
    for (const file of Array.from(files)) {
      try {
        await uploadCaseFile.mutateAsync({ file, description: `Case file: ${file.name}` });
        uploaded++;
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[CaseDetailView] File upload failed:', err);
        }
      }
    }
    if (caseFileInputRef.current) caseFileInputRef.current.value = '';
    if (uploaded > 0) {
      setActivityToast({ message: `${uploaded} file${uploaded > 1 ? 's' : ''} uploaded`, type: 'success' });
    }
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
    setEditLookupLabel('');
  };
  const saveEdit = async (fieldKey: string) => {
    try {
      if (updateCaseField.isPending) return;
      let normalizedEditValue = editValue;
      let normalizedOwnerLabel = editLookupLabel;
      const payload =
        fieldKey === 'owner'
          ? await (async () => {
              let ownerValue = String(editValue ?? '').trim();
              let ownerLabel =
                String(editLookupLabel ?? '').trim() ||
                ownerLabelByValue.get(ownerValue) ||
                '';
              if (ownerValue && !ownerLabel) {
                const byLabel = ownerLookupOptions.find(
                  (opt) => String(opt.label ?? '').trim().toLowerCase() === ownerValue.toLowerCase()
                );
                if (byLabel?.value) {
                  ownerValue = String(byLabel.value).trim();
                  ownerLabel = String(byLabel.label ?? '').trim();
                }
              }
              if (ownerValue && looksLikeGraphUserSfId(ownerValue)) {
                const resolvedOwner = await findGraphUserBySfId(ownerValue);
                ownerValue = String(resolvedOwner?.key ?? ownerValue).trim();
                if (!ownerLabel) ownerLabel = String(resolvedOwner?.displayName ?? '').trim();
              }
              normalizedEditValue = ownerValue;
              normalizedOwnerLabel = ownerLabel;
              return buildOwnerUpdatePayload(ownerValue, ownerLabel);
            })()
          : (() => {
              const fieldApiNames = fieldApiMap[fieldKey];
              if (!fieldApiNames?.length) return null;
              return fieldApiNames
                .filter((name) => !!String(name ?? '').trim())
                .reduce((acc, name) => {
                  acc[name] = editValue || null;
                  return acc;
                }, {} as Record<string, string | null>);
            })();
      if (!payload) return;
      await updateCaseField.mutateAsync(payload);
      const oldValue = String(localCaseValues[fieldKey] ?? '').trim();
      const uiValue = fieldKey === 'owner' ? normalizedEditValue : editValue;
      setLocalCaseValues((prev) => ({ ...prev, [fieldKey]: uiValue }));
      if (fieldKey === 'owner') {
        setLocalOwnerLabel(String(normalizedOwnerLabel ?? '').trim());
        lastLocalOwnerSetRef.current = Date.now();
        if (String(normalizedEditValue ?? '').trim()) {
          setLocalCaseValues((prev) => ({ ...prev, status: 'Assigned' }));
          lastLocalStatusSetRef.current = Date.now();
        }
      }
      if (fieldKey === 'status') {
        lastLocalStatusSetRef.current = Date.now();
        await refreshCaseLocalFromServer().catch(() => {});
      }
      if (fieldKey === 'status') {
        const verified = await readCaseStatusFromServer();
        if (verified) {
          setStatusDebug({
            requested: editValue,
            server: verified.resolved,
            rawStatus: verified.rawStatus,
            rawCanonical: verified.rawCanonical,
            rawSuffixed: verified.rawSuffixed,
          });
          if (verified.resolved) {
            setLocalCaseValues((prev) => ({ ...prev, status: verified.resolved }));
          }
          if ((verified.resolved || '').trim() !== editValue.trim()) {
            setWorkflowError(
              `Status save mismatch. Requested "${editValue}" but server returned "${verified.resolved || 'empty'}".`
            );
          } else {
            setWorkflowError(null);
          }
        }
      } else {
        setWorkflowError(null);
      }
      setEditingField(null);
      setEditValue('');
      setEditLookupLabel(fieldKey === 'owner' ? normalizedOwnerLabel : '');
      // Log field edit as activity
      const activityNewValue =
        fieldKey === 'owner'
          ? (String(normalizedOwnerLabel ?? '').trim() || String(normalizedEditValue ?? '').trim())
          : editValue;
      if (recordIdForActivity && oldValue !== activityNewValue) {
        const activityPayload = {
          type: 'note' as const,
          recordId: recordIdForActivity,
          recordType: 'case' as const,
          userId: String(c.ownerId ?? c.OwnerId__c ?? c.CreatedById__c ?? 'system'),
          userName: displayOwner || displayLastModifiedBy || displayCreatedBy || 'System',
          subject: `Field updated: ${fieldKey}`,
          description: `${fieldKey} changed from "${oldValue || '(empty)'}" to "${activityNewValue || '(empty)'}"`,
          source: 'manual' as const,
          sourceApp: 'credit_block' as const,
        };
        try {
          await createActivity(activityPayload);
        } catch {
          // Retry once with safe user fallback to avoid dropping owner assignment logs.
          await createActivity({
            ...activityPayload,
            userId: 'system',
            userName: displayLastModifiedBy || displayCreatedBy || 'System',
          });
        }
        await refetchCaseActivities?.();
      }
    } catch (error) {
      setWorkflowError(error instanceof Error ? error.message : 'Failed to save case field.');
    }
  };
  const openCaseEditModal = () => {
    setCaseEditError(null);
    setCaseEditValues({
      owner: localCaseValues.owner ?? '',
      parentCase: localCaseValues.parentCase ?? '',
      contactName: localCaseValues.contactName ?? '',
      status: localCaseValues.status ?? '',
      caseType: localCaseValues.caseType ?? '',
      origin: localCaseValues.origin ?? '',
      subType: localCaseValues.subType ?? '',
      priority: localCaseValues.priority ?? '',
      reblockDate: localCaseValues.reblockDate ?? '',
      subject: localCaseValues.subject ?? '',
      description: localCaseValues.description ?? '',
      internalComments: localCaseValues.internalComments ?? '',
    });
    setCaseEditOwnerLabel(
      localOwnerLabel ||
        ownerLabelByValue.get(String(localCaseValues.owner ?? '').trim()) ||
        ownerLookupResolved?.displayName ||
        displayOwner ||
        owner ||
        ''
    );
    setCaseEditParentCaseLabel(parentCaseDisplayLabel || localCaseValues.parentCase || '');
    setIsCaseEditModalOpen(true);
  };
  const saveCaseEditModal = async () => {
    if (!resolvedCaseRecordId) {
      setCaseEditError('Missing case id.');
      return;
    }
    setCaseEditSaving(true);
    setCaseEditError(null);
    try {
      const changedEntries = Object.entries(caseEditValues).filter(([fieldKey, fieldValue]) => {
        const next = String(fieldValue ?? '').trim();
        const prev = String(localCaseValues[fieldKey] ?? '').trim();
        if (fieldKey === 'owner') return next !== prev;
        return next !== prev;
      });
      if (changedEntries.length === 0) {
        setIsCaseEditModalOpen(false);
        return;
      }
      const payload = changedEntries.reduce((acc, [fieldKey, fieldValue]) => {
        if (fieldKey === 'owner') {
          Object.assign(acc, buildOwnerUpdatePayload(fieldValue, caseEditOwnerLabel));
          return acc;
        }
        const apiNames = fieldApiMap[fieldKey] ?? [];
        for (const apiName of apiNames) acc[apiName] = fieldValue?.trim() ? fieldValue : null;
        return acc;
      }, {} as Record<string, string | null>);
      await updateCaseRecord(payload, { gobodoPropertyPatch: false });
      const changedValueMap = Object.fromEntries(changedEntries);
      setLocalCaseValues((prev) => ({ ...prev, ...changedValueMap }));
      if (Object.prototype.hasOwnProperty.call(changedValueMap, 'owner')) {
        const ownerLabelForUi = String(caseEditOwnerLabel ?? '').trim();
        if (ownerLabelForUi) setLocalOwnerLabel(ownerLabelForUi);
      }
      setParentCaseDisplayLabel(caseEditParentCaseLabel || caseEditValues.parentCase || '');
      await refreshCaseLocalFromServer().catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'case-detail-owner'] });
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'cases'] });
      // Log bulk edit as activity
      if (recordIdForActivity && changedEntries.length > 0) {
        const changes = changedEntries.map(([k, v]) => `${k}: "${v}"`).join(', ');
        createActivity({
          type: 'note',
          recordId: recordIdForActivity,
          recordType: 'case',
          userId: String(c.ownerId ?? c.OwnerId__c ?? c.CreatedById__c ?? 'system'),
          userName: displayOwner || 'System',
          subject: `Case updated (${changedEntries.length} field${changedEntries.length > 1 ? 's' : ''})`,
          description: `Fields changed: ${changes}`,
          source: 'manual',
          sourceApp: 'credit_block',
        }).catch(() => {});
        refetchCaseActivities?.();
      }
      setIsCaseEditModalOpen(false);
    } catch (error) {
      setCaseEditError(error instanceof Error ? error.message : 'Failed to save case updates.');
    } finally {
      setCaseEditSaving(false);
    }
  };
  const inputCls =
    'w-full border border-gray-300 dark:border-gray-600 rounded-md px-2.5 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400';
  const labelCls = 'text-xs font-medium text-gray-600 dark:text-gray-300';
  const recordIdForActivity = pickFirstText(
    resolvedCaseRecordId,
    c.key,
    c.id,
    caseKey,
    c.sf_id,
    c.salesforce_id,
    c.CaseId__c,
    c.case_id,
    c.caseId,
    caseNumber
  );
  const defaultActivitySubject = useMemo(() => {
    if (chatterAction === 'call') return 'Call';
    if (chatterAction === 'task') return `Task · ${caseNumber || 'Case'}`;
    if (chatterAction === 'event') return `Event · ${caseNumber || 'Case'}`;
    if (chatterAction === 'email') return `Email · ${caseNumber || 'Case'}`;
    return `Post · ${caseNumber || 'Case'}`;
  }, [chatterAction, caseNumber]);
  const {
    data: caseActivities = [],
    isLoading: caseActivitiesLoading,
    error: caseActivitiesError,
    refetch: refetchCaseActivities,
  } = useCreditBlockActivitiesForCase(
    recordIdForActivity,
    !!recordIdForActivity
  );
  const chatterActivityAction = (type: string) => {
    switch (type) {
      case 'email':
        return 'sent an email.';
      case 'task':
        return 'logged a task.';
      case 'meeting':
      case 'event':
        return 'logged an event.';
      case 'call':
        return 'logged a call.';
      default:
        return 'logged an activity.';
    }
  };
  useEffect(() => {
    setComposerError(null);
    setComposerSubject('');
    setComposerBody('');
    setComposerDueDate('');
    setComposerLocation('');
  }, [chatterAction]);
  useEffect(() => {
    if (!caseActivitiesError) return;
    setActivityToast({
      message: caseActivitiesError instanceof Error ? caseActivitiesError.message : 'Unable to load case activity feed',
      type: 'error',
    });
  }, [caseActivitiesError]);
  useEffect(() => {
    if (!activityToast) return;
    const timer = setTimeout(() => setActivityToast(null), 3000);
    return () => clearTimeout(timer);
  }, [activityToast]);
  useEffect(() => {
    const sfIds = new Set<string>();
    for (const activity of caseActivities) {
      const text = `${String(activity.subject ?? '')} ${String(activity.description ?? '')}`;
      const matches = text.match(/005[0-9A-Za-z]{12,15}/g) || [];
      matches.forEach((id) => sfIds.add(id));
    }
    const unresolved = Array.from(sfIds).filter((id) => !resolvedActivityUserNames[id]);
    if (unresolved.length === 0) return;
    let cancelled = false;
    void Promise.all(
      unresolved.map(async (sfId) => {
        try {
          const row = await findGraphUserBySfId(sfId);
          const name = String(row?.displayName ?? '').trim();
          return { sfId, name };
        } catch {
          return { sfId, name: '' };
        }
      })
    ).then((rows) => {
      if (cancelled) return;
      setResolvedActivityUserNames((prev) => {
        const next = { ...prev };
        for (const row of rows) {
          if (row.name) next[row.sfId] = row.name;
        }
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [caseActivities, resolvedActivityUserNames]);
  const humanizeActivityText = (text: string): string => {
    const raw = String(text ?? '');
    if (!raw) return '';
    return raw.replace(/005[0-9A-Za-z]{12,15}/g, (sfId) => resolvedActivityUserNames[sfId] || sfId);
  };
  const createCaseActivity = async () => {
    if (!recordIdForActivity) return;
    const type = chatterAction === 'event' ? 'meeting' : chatterAction === 'post' ? 'note' : chatterAction;
    const subjectValue = (composerSubject || defaultActivitySubject).trim();
    const normalizedDueDate = composerDueDate.trim();
    const scheduledAt =
      normalizedDueDate && (chatterAction === 'task' || chatterAction === 'event')
        ? (() => {
            if (chatterAction === 'task') {
              return normalizedDueDate.length === 10 ? `${normalizedDueDate}T00:00:00` : normalizedDueDate;
            }
            return normalizedDueDate;
          })()
        : undefined;
    const descriptionBits = [composerBody.trim()];
    if (normalizedDueDate) descriptionBits.push(`Due: ${normalizedDueDate}`);
    if (composerLocation.trim()) descriptionBits.push(`Location: ${composerLocation.trim()}`);
    setComposerError(null);
    try {
      await createActivity({
        type,
        recordId: recordIdForActivity,
        recordType: 'case',
        userId: String(c.ownerId ?? c.OwnerId__c ?? c.CreatedById__c ?? c.createdBy ?? 'system'),
        userName: displayOwner || displayLastModifiedBy || displayCreatedBy || 'System',
        subject: subjectValue,
        description: descriptionBits.filter(Boolean).join('\n'),
        ...(scheduledAt ? { scheduledAt } : {}),
        source: 'manual',
        sourceApp: 'credit_block',
      });
      await refetchCaseActivities();
      setComposerSubject('');
      setComposerBody('');
      setComposerDueDate('');
      setComposerLocation('');
      setComposerSavedAt(Date.now());
      setSidebarTab('chatter');
    } catch (error: any) {
      setComposerError(error?.message || 'Unable to save activity');
    }
  };
  const renderEditableValue = (
    fieldKey: string,
    value: string,
    opts?: {
      isLink?: boolean;
      inputType?: 'text' | 'textarea' | 'date' | 'select' | 'lookup';
      options?: Array<string | { value: string; label: string }>;
      formatDate?: boolean;
      displayValue?: string;
    }
  ) => {
    const isEditing = editingField === fieldKey;
    const isLink = !!opts?.isLink;
    const inputType = opts?.inputType ?? 'text';
    const options = opts?.options ?? [];
    const displayValue = value || '';
    const optionObjects = options
      .map((opt) => (typeof opt === 'string' ? { value: opt, label: opt } : opt))
      .filter((opt) => !!String(opt?.value ?? '').trim());
    const optionMap = new Map(optionObjects.map((opt) => [String(opt.value), String(opt.label)]));
    if (displayValue && !optionMap.has(displayValue)) optionMap.set(displayValue, displayValue);
    const mergedOptions = Array.from(optionMap.entries()).map(([optValue, optLabel]) => ({
      value: optValue,
      label: optLabel,
    }));
    const fieldSaving = updateCaseField.isPending && editingField === fieldKey;
    if (isEditing) {
      return (
        <div className="flex items-center gap-2">
          {inputType === 'textarea' ? (
            <textarea
              rows={3}
              className={inputCls}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
            />
          ) : inputType === 'lookup' ? (
            <div className="flex-1">
              <StandardLookupField
                field={ownerLookupField}
                value={String(editValue || '')}
                label=""
                compact
                selectedOptionLabel={editLookupLabel || opts?.displayValue || optionMap.get(displayValue) || displayValue}
                onChange={(value) => setEditValue(value)}
                onLookupChange={(selection) => {
                  setEditValue(selection?.value ?? '');
                  setEditLookupLabel(selection?.label ?? '');
                }}
                userOptionsOverride={ownerLookupOptions}
              />
            </div>
          ) : inputType === 'select' ? (
            <select
              className={inputCls}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
            >
              <option value="">—</option>
              {mergedOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : (
            <input
              type={inputType}
              className={inputCls}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
            />
          )}
          <button
            type="button"
            onClick={() => saveEdit(fieldKey)}
            disabled={fieldSaving}
            className="p-1 text-emerald-600 hover:text-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={fieldSaving ? 'Saving...' : 'Save'}
          >
            {fieldSaving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={cancelEdit}
            disabled={fieldSaving}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Cancel"
          >
            <X className="h-4 w-4" />
          </button>
          {fieldSaving && <span className="text-xs text-blue-600 dark:text-blue-400">Saving...</span>}
        </div>
      );
    }
    const shownRaw =
      opts?.displayValue ??
      (inputType === 'lookup' ? (optionMap.get(displayValue) ?? displayValue) : displayValue);
    const shown = opts?.formatDate ? (shownRaw ? fmtDate(shownRaw) : '') : shownRaw;
    return (
      <div className="group flex items-center gap-2 min-h-[1.75rem]">
        <div className={`text-sm flex-1 ${isLink ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'}`}>
          {shown || '—'}
        </div>
        <button
          type="button"
          onClick={() => startEdit(fieldKey, displayValue)}
          className="p-0.5 text-gray-400 hover:text-blue-600 opacity-70 group-hover:opacity-100 transition-opacity"
          title="Edit"
          disabled={updateCaseField.isPending}
        >
          <Edit3 className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  };
  // Derive case history from the case record's own audit fields.
  // CaseHistory table doesn't exist in RDS — SF doesn't replicate it.
  // These entries reflect actual synced field values on the Case record.
  // Also check for upstream CaseHistory array if ever provided.
  const rawCaseHistory = Array.isArray(c.caseHistory) ? c.caseHistory
    : Array.isArray(c.CaseHistory) ? c.CaseHistory : [];

  const caseHistoryRows = rawCaseHistory.length > 0
    ? rawCaseHistory.map((row: any, idx: number) => ({
        id: String(row.id ?? row.Id ?? `h-${idx}`),
        date: fmtDate(row.date ?? row.createdDate ?? row.CreatedDate ?? row.timestamp ?? ''),
        field: String(row.field ?? row.Field ?? ''),
        user: String(row.user ?? row.createdBy ?? row.CreatedBy ?? row.User ?? ''),
        oldValue: String(row.oldValue ?? row.OldValue ?? row.old ?? '—'),
        newValue: String(row.newValue ?? row.NewValue ?? row.new ?? '—'),
      }))
    : [
        // Derive from case record audit fields
        createdDate && {
          id: 'h-created',
          date: fmtDate(createdDate),
          field: 'Created',
          user: displayCreatedBy,
          oldValue: '—',
          newValue: subject || caseNumber || 'Case created',
        },
        (lastModifiedDate && lastModifiedDate !== createdDate) && {
          id: 'h-modified',
          date: fmtDate(lastModifiedDate),
          field: 'Status',
          user: displayLastModifiedBy,
          oldValue: '—',
          newValue: status || '—',
        },
        displayOrigin && {
          id: 'h-origin',
          date: fmtDate(createdDate),
          field: 'Case Origin',
          user: displayCreatedBy,
          oldValue: '—',
          newValue: displayOrigin,
        },
        displayOwner && {
          id: 'h-owner',
          date: fmtDate(createdDate),
          field: 'Owner',
          user: 'System',
          oldValue: '—',
          newValue: displayOwner,
        },
      ].filter(Boolean);

  // Dev diagnostics (guarded)
  if (process.env.NODE_ENV === 'development') {
    console.debug('[CaseDetailView] caseId:', c.id, 'key:', c.key,
      'historyRows:', caseHistoryRows.length,
      'Data pipeline: Case synced from RDS cases table via Case__c.',
      'Related objects still pending: attachments, case_history, emails, notes.',
      'Case activity uses synced Task/Event custom objects when available.');
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
      {/* Back */}
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-5 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded bg-red-600 text-white flex-shrink-0">
              <span className="text-xs font-bold">CS</span>
            </span>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Case</p>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">{subject || caseNumber}</h2>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" disabled className="px-3 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed inline-flex items-center gap-1" title="Coming soon">
              <Plus className="h-3 w-3" /> Follow
            </button>
            <button
              type="button"
              onClick={openCaseEditModal}
              className="px-3 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => { void moveCaseToInProgress(); }}
              disabled={workflowBusy || !!(localCaseValues.status && !['new', 'assigned'].includes(localCaseValues.status.toLowerCase()))}
              className={`px-3 py-1.5 text-xs font-medium border rounded inline-flex items-center gap-1 ${
                workflowBusy || (localCaseValues.status && !['new', 'assigned'].includes(localCaseValues.status.toLowerCase()))
                  ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30'
              }`}
              title="Change status from New/Assigned to In Progress"
            >
              <Clock className="h-3 w-3" /> In Progress
            </button>
            <button
              type="button"
              onClick={() => {
                void resolveUnlockCase();
              }}
              disabled={workflowBusy || !linkedUnblockRequestsAllCompleted || !linkedBureausAllUnblocked}
              className={`px-3 py-1.5 text-xs font-medium border rounded inline-flex items-center gap-1 ${
                workflowBusy || !linkedUnblockRequestsAllCompleted || !linkedBureausAllUnblocked
                  ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/30'
              }`}
              title={
                linkedUnblockRequestsAllCompleted && linkedBureausAllUnblocked
                  ? 'Mark case as resolved'
                  : 'Resolve is enabled once linked unblock requests are completed and requested bureaus are unblocked'
              }
            >
              <CheckCircle className="h-3 w-3" /> Mark as Resolved
            </button>
            <button
              type="button"
              onClick={() => {
                void closeCaseWithComment();
              }}
              disabled={workflowBusy || (localCaseValues.status ?? '').toLowerCase() === 'closed'}
              className={`px-3 py-1.5 text-xs font-medium border rounded inline-flex items-center gap-1 ${
                workflowBusy || (localCaseValues.status ?? '').toLowerCase() === 'closed'
                  ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30'
              }`}
              title="Close case (requires a closing comment)"
            >
              <CheckCircle className="h-3 w-3" /> Close Case
            </button>
            <button
              type="button"
              onClick={openTransferModal}
              className="px-3 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 inline-flex items-center gap-1"
            >
              <ArrowRightLeft className="h-3 w-3" /> Transfer Case
            </button>
          </div>
        </div>
      </div>

      {/* Status Path */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-5 py-3">
        <CaseStatusPath currentStatus={localCaseValues.status || status || 'New'} />
      </div>
      {(workflowError || workflowNotice || statusDebug) && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2 space-y-1">
          {workflowError ? (
            <p className="text-xs text-red-600 dark:text-red-400">{workflowError}</p>
          ) : null}
          {workflowNotice ? (
            <p className="text-xs text-emerald-600 dark:text-emerald-400">{workflowNotice}</p>
          ) : null}
          {statusDebug ? (
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              Status debug: requested="{statusDebug.requested}" server="{statusDebug.server}" raw(status="{statusDebug.rawStatus}", Status__c="{statusDebug.rawCanonical}", Status{suffix}="{statusDebug.rawSuffixed}")
            </p>
          ) : null}
        </div>
      )}

      {/* Two-column: details + sidebar */}
      <div className="flex flex-col lg:flex-row gap-4 items-start">
        {/* Left: Detail sections */}
        <div className="flex-1 min-w-0 space-y-4">
          <div className="flex items-center gap-5 border-b border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setRecordTab('related')}
              className={`pb-2 text-sm font-medium ${
                recordTab === 'related'
                  ? 'text-gray-900 dark:text-gray-100 border-b-2 border-blue-600'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              Related
            </button>
            <button
              type="button"
              onClick={() => setRecordTab('details')}
              className={`pb-2 text-sm font-medium ${
                recordTab === 'details'
                  ? 'text-gray-900 dark:text-gray-100 border-b-2 border-blue-600'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              Details
            </button>
          </div>

          {recordTab === 'details' ? (
            <>
              {/* Case Information */}
              <SfSection title="Case Information">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                  <SfField label="Case Number" value={caseNumber} showEdit={false} />
                  <SfField
                    label="Case Owner"
                    value={renderEditableValue('owner', localCaseValues.owner ?? '', {
                      inputType: 'lookup',
                      options: ownerLookupOptions,
                      displayValue:
                        localOwnerLabel ||
                        ownerLabelByValue.get(localCaseValues.owner ?? '') ||
                        ownerLookupResolved?.displayName ||
                        displayOwner ||
                        owner ||
                        '',
                    })}
                    showEdit={false}
                  />
                  <SfField
                    label="Account Name"
                    value={
                      canOpenAccountClient ? (
                        <button
                          type="button"
                          onClick={() => onOpenClientDetails?.(accountLookupForNavigation)}
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {accountNameResolved || '—'}
                        </button>
                      ) : (
                        accountNameResolved
                      )
                    }
                    isLink={canOpenAccountClient}
                  />
                  <SfField label="Parent Case" value={renderEditableValue('parentCase', parentCaseDisplayLabel || localCaseValues.parentCase || '')} showEdit={false} />
                  <SfField label="Contact Name" value={renderEditableValue('contactName', localCaseValues.contactName ?? '', { isLink: true })} showEdit={false} />
                </div>
              </SfSection>

              {/* Additional Information */}
              <SfSection title="Additional Information">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                  <SfField label="Status" value={renderEditableValue('status', localCaseValues.status ?? '', { inputType: 'select', options: statusOptions })} showEdit={false} />
                  <SfField label="Type" value={renderEditableValue('caseType', localCaseValues.caseType ?? '', { inputType: 'select', options: typeOptions })} showEdit={false} />
                  <SfField label="Case Origin" value={renderEditableValue('origin', localCaseValues.origin ?? '', { inputType: 'select', options: originOptions })} showEdit={false} />
                  <SfField label="Sub-Type" value={renderEditableValue('subType', localCaseValues.subType ?? '', { inputType: 'select', options: subTypeOptions })} showEdit={false} />
                  <SfField label="Priority" value={renderEditableValue('priority', localCaseValues.priority ?? '', { inputType: 'select', options: priorityOptions })} showEdit={false} />
                  <SfField label="Reblock Date" value={renderEditableValue('reblockDate', localCaseValues.reblockDate ?? '', { inputType: 'date', formatDate: true })} showEdit={false} />
                </div>
              </SfSection>

              {/* Description Information */}
              <SfSection title="Description Information">
                <div className="space-y-4">
                  <SfField label="Subject" value={renderEditableValue('subject', localCaseValues.subject ?? '')} showEdit={false} />
                  <SfField label="Description" value={
                    renderEditableValue('description', localCaseValues.description ?? '', { inputType: 'textarea' })
                  } showEdit={false} />
                  <SfField label="Internal Comments" value={renderEditableValue('internalComments', localCaseValues.internalComments ?? '', { inputType: 'textarea' })} showEdit={false} />
                </div>
              </SfSection>

              {/* System Information */}
              <SfSection title="System Information">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                  <SfField label="Created By" value={
                    formatAuditUserDate(displayCreatedBy, createdDate) || undefined
                  } showEdit={false} />
                  <SfField label="Last Modified By" value={
                    formatAuditUserDate(displayLastModifiedBy, lastModifiedDate) || undefined
                  } showEdit={false} />
                  <SfField label="Date/Time Closed" value={closedDate ? fmtDate(closedDate) : undefined} showEdit={false} />
                </div>
              </SfSection>
            </>
          ) : (
            <div className="space-y-3">
              {/* ---- Case History (Gobodo traversal + derived from Case audit fields) ---- */}
              <RelatedSection title="Case History" count={gobodoCaseHistory.length || caseHistoryRows.length}>
                {(gobodoCaseHistory.length > 0 || caseHistoryRows.length > 0) ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                        <tr>
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-left">Field</th>
                          <th className="px-3 py-2 text-left">User</th>
                          <th className="px-3 py-2 text-left">Original Value</th>
                          <th className="px-3 py-2 text-left">New Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(gobodoCaseHistory.length > 0 ? gobodoCaseHistory : caseHistoryRows).map((row) => (
                          <tr key={row.id} className="border-b last:border-b-0 border-gray-100 dark:border-gray-700">
                            <td className="px-3 py-2">{row.date ? fmtDate(row.date) : '—'}</td>
                            <td className="px-3 py-2">{row.field}</td>
                            <td className="px-3 py-2 text-blue-600 dark:text-blue-400">{row.user}</td>
                            <td className="px-3 py-2">{row.oldValue}</td>
                            <td className="px-3 py-2">{row.newValue}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyRelatedState section="Case History" />
                )}
              </RelatedSection>

              {/* ---- Case Comments (from Gobodo CommentsApi) ---- */}
              <RelatedSection title="Case Comments" count={gobodoCaseComments.length} showNew>
                {/* Comment Composer */}
                <div className="px-3 pt-3 pb-2 border-b border-gray-100 dark:border-gray-700">
                  <textarea
                    rows={2}
                    className={`w-full text-xs border border-gray-200 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${closeCaseCommentRequired && !newCommentBody.trim() ? 'border-red-400 ring-1 ring-red-300' : ''}`}
                    placeholder={closeCaseCommentRequired ? 'A closing comment is required to close this case...' : 'Add a comment...'}
                    value={newCommentBody}
                    onChange={(e) => {
                      setNewCommentBody(e.target.value);
                      if (closeCaseCommentRequired && e.target.value.trim()) {
                        setCloseCaseCommentRequired(false);
                        setWorkflowError(null);
                      }
                    }}
                  />
                  <div className="flex justify-end mt-1.5 gap-2">
                    <button
                      type="button"
                      onClick={() => void submitCaseComment()}
                      disabled={commentSubmitting || !newCommentBody.trim()}
                      className="px-3 py-1.5 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {commentSubmitting ? 'Adding...' : 'Add Comment'}
                    </button>
                  </div>
                </div>
                {gobodoCaseComments.length > 0 ? (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {gobodoCaseComments.map((comment) => (
                      <div key={comment.id} className="px-3 py-2 text-xs">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-blue-600 dark:text-blue-400 font-medium">{comment.createdBy}</span>
                          <span className="text-gray-500">{comment.createdDate ? fmtDate(comment.createdDate) : ''}</span>
                        </div>
                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{comment.body}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyRelatedState section="Case Comments" />
                )}
              </RelatedSection>

              {/* ---- Related Cases — no parent_case_rel defined in Gobodo ---- */}
              <RelatedSection title="Related Cases" count={0} showNew>
                <EmptyRelatedState section="Related Cases" />
              </RelatedSection>

              <RelatedSection title="Case Activities" count={0}>
                <div className="p-3">
                  <SalesforceActivityTab
                    key={`case-related-${recordIdForActivity}-${composerSavedAt}`}
                    recordId={recordIdForActivity}
                    recordType="case"
                    contactName={accountNameResolved}
                    activities={caseActivities}
                    loading={caseActivitiesLoading}
                    disableQueryFetch
                    onLogActivity={(type) => {
                      setSidebarTab('chatter');
                      setChatterAction(mapToolbarActivityToComposer(type));
                    }}
                  />
                </div>
              </RelatedSection>

              {/* ---- Notes — 0 rows in RDS ---- */}
              <RelatedSection title="Notes" count={0} showNew>
                <EmptyRelatedState section="Notes" />
              </RelatedSection>

              {/* ---- Emails (from Gobodo via case_email_rel traversal) ---- */}
              <RelatedSection title="Emails" count={gobodoEmails.length}>
                {gobodoEmails.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                        <tr>
                          <th className="px-3 py-2 text-left">Subject</th>
                          <th className="px-3 py-2 text-left">From</th>
                          <th className="px-3 py-2 text-left">To</th>
                          <th className="px-3 py-2 text-left">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gobodoEmails.map((email) => (
                          <tr key={email.id} className="border-b last:border-b-0 border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-3 py-2 text-blue-600 dark:text-blue-400">{email.subject || '(no subject)'}</td>
                            <td className="px-3 py-2">{email.fromAddress || '—'}</td>
                            <td className="px-3 py-2">{email.toAddress || '—'}</td>
                            <td className="px-3 py-2">{email.messageDate ? fmtDate(email.messageDate) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyRelatedState section="Emails" />
                )}
              </RelatedSection>

              {/* ---- Files ---- */}
              <RelatedSection
                title="Files"
                count={caseFiles.length}
                showNew
                newLabel={uploadCaseFile.isPending ? 'Uploading...' : 'Add Files'}
                onNew={() => caseFileInputRef.current?.click()}
              >
                <input
                  ref={caseFileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => handleCaseFileUpload(e.target.files)}
                />
                {caseFilesLoading ? (
                  <div className="px-4 py-4 text-center text-sm text-gray-400">Loading files...</div>
                ) : caseFiles.length > 0 ? (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {caseFiles.map((f) => (
                      <div key={f.id} className="px-4 py-3 flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                          <Paperclip className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-900 dark:text-gray-100 truncate">{f.fileName}</span>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                          {f.fileSize ? `${(f.fileSize / 1024).toFixed(0)} KB` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyRelatedState section="files" />
                )}
              </RelatedSection>

              {/* ---- Attachments — 0 linked to cases in RDS ---- */}
              <RelatedSection title="Attachments" count={0}>
                <EmptyRelatedState section="Attachments" />
              </RelatedSection>
            </div>
          )}
        </div>

        {/* Right: Chatter / Activities sidebar */}
        <div className="w-full lg:w-[40rem] flex-shrink-0">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm">
            {/* Sidebar tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <button
                type="button"
                onClick={() => setSidebarTab('chatter')}
                className={`flex-1 px-4 py-3 text-sm font-semibold ${
                  sidebarTab === 'chatter'
                    ? 'text-gray-900 dark:text-gray-100 border-b-2 border-blue-600 dark:border-blue-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 border-b-2 border-transparent'
                }`}
              >
                Chatter
              </button>
              <button
                type="button"
                onClick={() => setSidebarTab('activities')}
                className={`flex-1 px-4 py-3 text-sm font-semibold ${
                  sidebarTab === 'activities'
                    ? 'text-gray-900 dark:text-gray-100 border-b-2 border-blue-600 dark:border-blue-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 border-b-2 border-transparent'
                }`}
              >
                Activities
              </button>
            </div>

            {/* Shared composer actions */}
            <div className="flex flex-wrap gap-0 text-xs border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              {[
                { id: 'email', label: 'Email' },
                { id: 'post', label: 'Post' },
                { id: 'task', label: 'New Task' },
                { id: 'event', label: 'New Event' },
                { id: 'call', label: 'Log a Call' },
              ].map((action) => (
                <button
                  key={action.id}
                  type="button"
                  onClick={() => {
                    setSidebarTab('chatter');
                    setChatterAction(action.id as 'email' | 'post' | 'task' | 'event' | 'call');
                  }}
                  className={`px-3 py-2.5 border-r border-gray-200 dark:border-gray-700 last:border-r-0 transition-colors ${
                    chatterAction === action.id && sidebarTab === 'chatter'
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-semibold'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {action.label}
                </button>
              ))}
            </div>

            {sidebarTab === 'chatter' ? (
              <div className="p-4 bg-gray-50/50 dark:bg-gray-900/20">
                {/* Composer panel */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-md mb-4 bg-white dark:bg-gray-800">
                  {chatterAction === 'email' && (
                    <div className="p-3 space-y-2.5">
                      <div><p className={labelCls}>Subject</p><input className={inputCls} placeholder="Subject" value={composerSubject} onChange={(e) => setComposerSubject(e.target.value)} /></div>
                      <div><p className={labelCls}>Body</p><textarea rows={8} className={inputCls} placeholder="Compose email..." value={composerBody} onChange={(e) => setComposerBody(e.target.value)} /></div>
                      <div className="flex items-center justify-between">
                        <button type="button" className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                          <Paperclip className="h-3.5 w-3.5" /> Attach
                        </button>
                        <button type="button" onClick={createCaseActivity} disabled={creatingActivity} className="px-3 py-1.5 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
                          {creatingActivity ? 'Saving...' : 'Send'}
                        </button>
                      </div>
                    </div>
                  )}

                  {chatterAction === 'post' && (
                    <div className="p-3 space-y-2.5">
                      <p className={labelCls}>Post</p>
                      <textarea rows={4} className={inputCls} placeholder="Share an update..." value={composerBody} onChange={(e) => setComposerBody(e.target.value)} />
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">To this case</span>
                        <button type="button" onClick={createCaseActivity} disabled={creatingActivity} className="px-3 py-1.5 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
                          {creatingActivity ? 'Saving...' : 'Share'}
                        </button>
                      </div>
                    </div>
                  )}

                  {chatterAction === 'task' && (
                    <div className="p-3 space-y-2.5">
                      <div><p className={labelCls}>Subject</p><input className={inputCls} placeholder="Subject" value={composerSubject} onChange={(e) => setComposerSubject(e.target.value)} /></div>
                      <div className="relative">
                        <p className={labelCls}>Due Date</p>
                        <input type="date" className={`${inputCls} pr-8`} value={composerDueDate} onChange={(e) => setComposerDueDate(e.target.value)} />
                        <Calendar className="h-3.5 w-3.5 absolute right-2 top-2 text-gray-400 pointer-events-none" />
                      </div>
                      <div><p className={labelCls}>Comments</p><textarea rows={3} className={inputCls} placeholder="Task details" value={composerBody} onChange={(e) => setComposerBody(e.target.value)} /></div>
                      <div className="flex justify-end">
                        <button type="button" onClick={createCaseActivity} disabled={creatingActivity} className="px-3 py-1.5 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
                          {creatingActivity ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  )}

                  {chatterAction === 'event' && (
                    <div className="p-3 space-y-2.5">
                      <div><p className={labelCls}>Subject</p><input className={inputCls} placeholder="Subject" value={composerSubject} onChange={(e) => setComposerSubject(e.target.value)} /></div>
                      <div><p className={labelCls}>Description</p><textarea rows={3} className={inputCls} placeholder="Description" value={composerBody} onChange={(e) => setComposerBody(e.target.value)} /></div>
                      <div><p className={labelCls}>Start</p><input type="datetime-local" className={inputCls} value={composerDueDate} onChange={(e) => setComposerDueDate(e.target.value)} /></div>
                      <div><p className={labelCls}>Location</p><input className={inputCls} placeholder="Location" value={composerLocation} onChange={(e) => setComposerLocation(e.target.value)} /></div>
                      <div className="flex justify-end">
                        <button type="button" onClick={createCaseActivity} disabled={creatingActivity} className="px-3 py-1.5 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
                          {creatingActivity ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  )}

                  {chatterAction === 'call' && (
                    <div className="p-3 space-y-2.5">
                      <div><p className={labelCls}>Subject</p><input className={inputCls} placeholder="Subject" value={composerSubject} onChange={(e) => setComposerSubject(e.target.value)} /></div>
                      <div><p className={labelCls}>Comments</p><textarea rows={3} className={inputCls} placeholder="Comments" value={composerBody} onChange={(e) => setComposerBody(e.target.value)} /></div>
                      <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                        <input type="checkbox" className="rounded border-gray-300 dark:border-gray-600" />
                        Voicemail
                      </label>
                      <div className="flex justify-end">
                        <button type="button" onClick={createCaseActivity} disabled={creatingActivity} className="px-3 py-1.5 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
                          {creatingActivity ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {composerError && (
                  <p className="mb-3 text-xs text-red-600 dark:text-red-400">{composerError}</p>
                )}

                {/* Feed controls + feed */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="relative flex-1">
                    <Search className="h-3.5 w-3.5 absolute left-2 top-2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search this feed..."
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md pl-7 pr-2 py-2 text-xs bg-white dark:bg-gray-900"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => { void refetchCaseActivities(); }}
                    className="p-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                  </button>
                </div>

                {(createdDate || lastModifiedDate) ? (
                  <>
                    {caseActivitiesLoading ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-3">Loading activity feed...</p>
                    ) : (
                      caseActivities.slice(0, 20).map((a) => (
                        <FeedItem
                          key={`activity-${a.id}`}
                          author={a.userName || a.userId || 'System'}
                          action={chatterActivityAction(a.type)}
                          date={fmtDate(a.createdAt)}
                        >
                          {(a.subject || a.description) ? (
                            <div className="space-y-1">
                              {a.subject ? <div className="font-medium">{humanizeActivityText(a.subject)}</div> : null}
                              {a.description ? <div>{humanizeActivityText(a.description)}</div> : null}
                            </div>
                          ) : null}
                        </FeedItem>
                      ))
                    )}
                    {lastModifiedDate && lastModifiedDate !== createdDate && (
                      <FeedItem
                        author={displayLastModifiedBy}
                        action="updated this record."
                        date={fmtDate(lastModifiedDate)}
                      >
                        {status && (
                          <div>
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status</div>
                            <div>{status}</div>
                          </div>
                        )}
                      </FeedItem>
                    )}
                    {createdDate && (
                      <FeedItem
                        author={displayCreatedBy}
                        action="created this case."
                        date={fmtDate(createdDate)}
                      />
                    )}
                    <div className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
                      No more past activities to load.
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-6">No feed items yet.</p>
                )}
              </div>
            ) : (
              <div className="p-4">
                <SalesforceActivityTab
                  key={`case-sidebar-${recordIdForActivity}-${composerSavedAt}`}
                  recordId={recordIdForActivity}
                  recordType="case"
                  contactName={accountNameResolved}
                  activities={caseActivities}
                  loading={caseActivitiesLoading}
                  disableQueryFetch
                  onLogActivity={(type) => {
                    setSidebarTab('chatter');
                    setChatterAction(mapToolbarActivityToComposer(type));
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {isCaseEditModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/35 flex items-start justify-center px-4 py-6 overflow-y-auto">
          <div className="w-full max-w-5xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100">Edit {caseNumber}</h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">* = Required Information</span>
            </div>
            <div className="p-6 space-y-6">
              <SfSection title="Case Information">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  <div>
                    <p className={labelCls}>Case Number</p>
                    <input className={inputCls} value={caseNumber} disabled />
                  </div>
                  <div>
                    <p className={labelCls}>Case Owner</p>
                    <StandardLookupField
                      field={ownerLookupField}
                      value={caseEditValues.owner ?? ''}
                      label=""
                      selectedOptionLabel={
                        caseEditOwnerLabel ||
                        localOwnerLabel ||
                        ownerLabelByValue.get(String(caseEditValues.owner ?? '').trim()) ||
                        ownerLookupResolved?.displayName ||
                        displayOwner ||
                        owner ||
                        ''
                      }
                      onChange={(value) => setCaseEditValues((prev) => ({ ...prev, owner: value }))}
                      onLookupChange={(selection) => {
                        setCaseEditValues((prev) => ({ ...prev, owner: selection?.value ?? '' }));
                        setCaseEditOwnerLabel(selection?.label ?? '');
                      }}
                      userOptionsOverride={ownerLookupOptions}
                    />
                  </div>
                  <div>
                    <p className={labelCls}>Account Name</p>
                    <input className={inputCls} value={accountNameResolved} disabled />
                  </div>
                  <div>
                    <p className={labelCls}>Parent Case</p>
                    <StandardLookupField
                      field={parentCaseLookupField}
                      value={caseEditValues.parentCase ?? ''}
                      selectedOptionLabel={caseEditParentCaseLabel || caseEditValues.parentCase || ''}
                      onChange={(value) => setCaseEditValues((prev) => ({ ...prev, parentCase: value }))}
                      onLookupChange={(selection) => {
                        setCaseEditValues((prev) => ({ ...prev, parentCase: selection?.value ?? '' }));
                        setCaseEditParentCaseLabel(selection?.label ?? '');
                      }}
                    />
                  </div>
                  <div className="relative">
                    <p className={labelCls}>Contact Name</p>
                    <input
                      className={`${inputCls} pr-8`}
                      placeholder="Search Contacts..."
                      value={caseEditValues.contactName ?? ''}
                      onChange={(e) => setCaseEditValues((prev) => ({ ...prev, contactName: e.target.value }))}
                    />
                    <Search className="h-4 w-4 absolute right-2 top-[33px] text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </SfSection>

              <SfSection title="Additional Information">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  <div>
                    <p className={labelCls}>Status</p>
                    <select className={inputCls} value={caseEditValues.status ?? ''} onChange={(e) => setCaseEditValues((prev) => ({ ...prev, status: e.target.value }))}>
                      <option value="">—None—</option>
                      {statusOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className={labelCls}>Type</p>
                    <select className={inputCls} value={caseEditValues.caseType ?? ''} onChange={(e) => setCaseEditValues((prev) => ({ ...prev, caseType: e.target.value }))}>
                      <option value="">—None—</option>
                      {typeOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className={labelCls}>Case Origin</p>
                    <select className={inputCls} value={caseEditValues.origin ?? ''} onChange={(e) => setCaseEditValues((prev) => ({ ...prev, origin: e.target.value }))}>
                      <option value="">—None—</option>
                      {originOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className={labelCls}>Sub-Type</p>
                    <select className={inputCls} value={caseEditValues.subType ?? ''} onChange={(e) => setCaseEditValues((prev) => ({ ...prev, subType: e.target.value }))}>
                      <option value="">—None—</option>
                      {subTypeOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className={labelCls}>Priority</p>
                    <select className={inputCls} value={caseEditValues.priority ?? ''} onChange={(e) => setCaseEditValues((prev) => ({ ...prev, priority: e.target.value }))}>
                      <option value="">—None—</option>
                      {priorityOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className={labelCls}>Reblock Date</p>
                    <input type="date" className={inputCls} value={caseEditValues.reblockDate ?? ''} onChange={(e) => setCaseEditValues((prev) => ({ ...prev, reblockDate: e.target.value }))} />
                  </div>
                </div>
              </SfSection>

              <SfSection title="Description Information">
                <div className="space-y-4">
                  <div>
                    <p className={labelCls}>Subject</p>
                    <input className={inputCls} value={caseEditValues.subject ?? ''} onChange={(e) => setCaseEditValues((prev) => ({ ...prev, subject: e.target.value }))} />
                  </div>
                  <div>
                    <p className={labelCls}>Description</p>
                    <textarea rows={3} className={inputCls} value={caseEditValues.description ?? ''} onChange={(e) => setCaseEditValues((prev) => ({ ...prev, description: e.target.value }))} />
                  </div>
                  <div>
                    <p className={labelCls}>Internal Comments</p>
                    <textarea rows={3} className={inputCls} value={caseEditValues.internalComments ?? ''} onChange={(e) => setCaseEditValues((prev) => ({ ...prev, internalComments: e.target.value }))} />
                  </div>
                </div>
              </SfSection>

              <SfSection title="System Information">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  <div>
                    <p className={labelCls}>Created By</p>
                    <input className={inputCls} value={formatAuditUserDate(displayCreatedBy, createdDate)} disabled />
                  </div>
                  <div>
                    <p className={labelCls}>Last Modified By</p>
                    <input className={inputCls} value={formatAuditUserDate(displayLastModifiedBy, lastModifiedDate)} disabled />
                  </div>
                  <div>
                    <p className={labelCls}>Date/Time Closed</p>
                    <input className={inputCls} value={closedDate ? fmtDate(closedDate) : ''} disabled />
                  </div>
                </div>
              </SfSection>

              {caseEditError && <p className="text-xs text-red-600 dark:text-red-400">{caseEditError}</p>}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsCaseEditModalOpen(false)}
                className="px-4 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled
                className="px-4 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-400 dark:text-gray-500 cursor-not-allowed"
                title="Not implemented yet"
              >
                Save &amp; New
              </button>
              <button
                type="button"
                onClick={() => { void saveCaseEditModal(); }}
                disabled={caseEditSaving}
                className="px-4 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {caseEditSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activityToast && (
        <div className="fixed bottom-6 right-6 z-50 animate-[slideUp_0.2s_ease-out]">
          <div className={`flex items-center gap-3 px-5 py-3 rounded-lg shadow-lg text-sm font-medium text-white ${
            activityToast.type === 'success' ? 'bg-emerald-600' : 'bg-red-600'
          }`}>
            {activityToast.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
            {activityToast.message}
            <button onClick={() => setActivityToast(null)} className="ml-2 hover:opacity-70">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
      {/* Transfer Case modal */}
      {transferModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={closeTransferModal}>
          <div className="absolute inset-0 bg-black/30" />
          <div
            className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Transfer Case</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                {caseNumber} — {subject}
              </p>
            </div>
            <div className={`px-6 py-4 ${transferSaving ? 'opacity-50 pointer-events-none' : ''}`}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">CB team member</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Transfer is limited to active Credit Block team members.
              </p>
              <div className="mt-2 max-h-52 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                {transferTeamUsersLoading ? (
                  <p className="px-4 py-6 text-sm text-gray-400 text-center">Loading CB team members...</p>
                ) : transferTeamOptions.length > 0 ? (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {transferTeamOptions.map((r) => (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => handleTransferSelect(r)}
                        className="w-full text-left px-4 py-3 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-900 dark:text-gray-100 transition-colors"
                      >
                        <span className="block">{r.label}</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          {r.roleLabel}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="px-4 py-6 text-sm text-gray-400 text-center">
                    No active CB team members found from Credit Block role assignments
                  </p>
                )}
              </div>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                type="button"
                onClick={closeTransferModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseDetailView;
